import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  dbSectionTitle: 'Databases',
  dbSectionIntro:
    'Choose who can use each database in Chats and Apps. You can change it here at any time.',
  dbAccessOff: 'Access is off',
  dbAccessEnabled: 'Access enabled for {count} of {total}',
  dbLoading: 'Loading databases…',
  dbLoadError: 'We couldn’t load your databases. Check your connection and try again.',
  queryLimitLabel: 'Longest query time',
  queryLimitHint:
    'How long one read may run before it stops. Chats and Apps can ask for up to this; otherwise a read stops after {default}.',
  queryLimitDefault: 'Default ({value})',
  queryLimitSource: 'Connection setting ({value})',
  queryLimitSaving: 'Saving…',
  queryLimitSaved: 'Saved',
  queryLimitError: 'We couldn’t save the time limit. Try again.',
  queryLimitLoadError: 'We couldn’t load the time limit.',
  queryLimitRetry: 'Try again',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'TLS verification failed. Check the server certificate or add its CA certificate under Advanced TLS.',
  pgAuthFailed: 'PostgreSQL rejected these credentials. Check the username and password.',
  pgNetworkFailed: 'Poneva could not reach this host. Check the hostname, port, and firewall.',
  pgReadOnlyRequired:
    'Use a read-only replica or a dedicated database account without administrator, schema-creation or role-switching permissions.',
  pgNoTables: 'No readable tables were found. Choose a database with at least one table.',
  pgCatalogLarge:
    'This database catalogue is too large. Narrow the database or schema before retrying.',
  pgCatalogChanged: 'The database catalogue changed. Test the connection again.',
  pgUnsafeHost: 'This host is not allowed. Use a public TLS PostgreSQL endpoint.',
  pgSetupBusy: 'Another PostgreSQL setup is already in progress. Try again in a moment.',
  invalidRequest: 'Check the fields and try again.',
  connectionChanged: 'This connection changed elsewhere. Reload it and try again.',
  genericStepError: 'We could not complete this step. Try again.',
  authNone: 'No sign-in',
  authBearer: 'Access key',
  authHeaders: 'Custom headers',
  authBasic: 'Username and password',
  authOauth: 'Sign in with the service',
  authShopify: 'Shopify Developer App',
  transportBuiltin: 'Run by Poneva',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Legacy SSE',
  transportAuto: 'Auto-detected',
  statusNeedsAuth: 'Needs sign-in',
  statusUnavailable: 'Unavailable',
  statusConnected: 'Connected',
  mcpAuthRequired: 'The service rejected these sign-in details. Check them and try again.',
  mcpUrlBlocked:
    'Use a public web address that starts with https://. Private network addresses are not supported.',
  mcpTimeout: 'The service took too long to respond. Check the address and try again.',
  mcpConnectionFailed: 'We could not connect. Check the web address and sign-in details.',
  mcpAuthorizationRequired: 'Sign in again to reconnect this service.',
  oauthNotConfigured:
    'Browser sign-in is not configured on this Poneva installation. Contact your administrator.',
  mcpInvalidRequest:
    'Check the web address and fill in the details for the selected sign-in method.',
  shopInvalid: 'Enter your store’s myshopify.com address, for example your-store.myshopify.com.',
  shopNotFound: 'Shopify has no store at that myshopify.com address.',
  shopNotPermitted:
    'This app cannot be used on that store. Install the app on the store from Shopify’s Dev Dashboard, and make sure the app and the store belong to the same Shopify organization.',
  shopNotInstalled:
    'The app isn’t installed on this store yet. In Shopify’s Dev Dashboard, open the app, choose Install app and pick this store, then try again.',
  shopCredentials:
    'Shopify rejected the client ID or secret. Copy both again from the app’s settings in Shopify’s Dev Dashboard.',
  shopUnavailable: 'Shopify could not be reached. Try again in a moment.',
  mcpGenericError: 'We could not complete that request. Try again.',
} as const;
type Copy = Record<keyof typeof en, string>;
const bg: Copy = {
  dbSectionTitle: 'Бази данни',
  dbSectionIntro:
    'Изберете кой може да използва всяка база данни в чатовете и приложенията. Можете да промените това тук по всяко време.',
  dbAccessOff: 'Достъпът е изключен',
  dbAccessEnabled: 'Достъпът е разрешен за {count} от {total}',
  dbLoading: 'Зареждане на базите данни…',
  dbLoadError: 'Не успяхме да заредим базите ви данни. Проверете връзката си и опитайте отново.',
  queryLimitLabel: 'Максимално време за заявка',
  queryLimitHint:
    'Колко дълго може да се изпълнява едно четене, преди да бъде прекратено. Чатовете и приложенията могат да заявят до това време; в противен случай четенето се прекратява след {default}.',
  queryLimitDefault: 'По подразбиране ({value})',
  queryLimitSource: 'Настройка на връзката ({value})',
  queryLimitSaving: 'Запазване…',
  queryLimitSaved: 'Запазено',
  queryLimitError: 'Не успяхме да запазим ограничението за време. Опитайте отново.',
  queryLimitLoadError: 'Не успяхме да заредим ограничението за време.',
  queryLimitRetry: 'Опитайте отново',
  seconds: '{count} сек.',
  minutes: '{count} мин.',
  pgTlsFailed:
    'Проверката на TLS не бе успешна. Проверете сертификата на сървъра или добавете неговия CA сертификат в „Разширени настройки за TLS“.',
  pgAuthFailed: 'PostgreSQL отхвърли тези данни за вход. Проверете потребителското име и паролата.',
  pgNetworkFailed:
    'Poneva не успя да се свърже с този хост. Проверете името на хоста, порта и защитната стена.',
  pgReadOnlyRequired:
    'Използвайте реплика само за четене или специален акаунт за база данни без разрешения за администриране, създаване на схеми или превключване на роли.',
  pgNoTables:
    'Не бяха намерени таблици, достъпни за четене. Изберете база данни с поне една таблица.',
  pgCatalogLarge:
    'Каталогът на тази база данни е твърде голям. Стеснете обхвата на базата данни или схемата, преди да опитате отново.',
  pgCatalogChanged: 'Каталогът на базата данни се промени. Тествайте връзката отново.',
  pgUnsafeHost: 'Този хост не е разрешен. Използвайте публична TLS крайна точка за PostgreSQL.',
  pgSetupBusy: 'Друга настройка на PostgreSQL вече се изпълнява. Опитайте отново след малко.',
  invalidRequest: 'Проверете полетата и опитайте отново.',
  connectionChanged: 'Тази връзка е променена от друго място. Презаредете я и опитайте отново.',
  genericStepError: 'Не успяхме да завършим тази стъпка. Опитайте отново.',
  authNone: 'Без вход',
  authBearer: 'Ключ за достъп',
  authHeaders: 'Персонализирани заглавки',
  authBasic: 'Потребителско име и парола',
  authOauth: 'Вход с услугата',
  authShopify: 'Приложение за разработчици на Shopify',
  transportBuiltin: 'Изпълнява се от Poneva',
  transportHttp: 'HTTP с поточно предаване',
  transportSse: 'SSE за съвместимост',
  transportAuto: 'Автоматично открито',
  statusNeedsAuth: 'Изисква вход',
  statusUnavailable: 'Недостъпно',
  statusConnected: 'Свързано',
  mcpAuthRequired: 'Услугата отхвърли тези данни за вход. Проверете ги и опитайте отново.',
  mcpUrlBlocked:
    'Използвайте публичен уеб адрес, който започва с https://. Адреси в частни мрежи не се поддържат.',
  mcpTimeout: 'Услугата отговори твърде дълго. Проверете адреса и опитайте отново.',
  mcpConnectionFailed: 'Не успяхме да се свържем. Проверете уеб адреса и данните за вход.',
  mcpAuthorizationRequired: 'Влезте отново, за да свържете тази услуга.',
  oauthNotConfigured:
    'Входът през браузър не е конфигуриран в тази инсталация на Poneva. Свържете се с администратора си.',
  mcpInvalidRequest: 'Проверете уеб адреса и попълнете данните за избрания метод на вход.',
  shopInvalid: 'Въведете адреса на магазина си в myshopify.com, например your-store.myshopify.com.',
  shopNotFound: 'Shopify не разполага с магазин на този адрес в myshopify.com.',
  shopNotPermitted:
    'Това приложение не може да се използва в този магазин. Инсталирайте приложението в магазина от таблото за разработчици на Shopify и се уверете, че приложението и магазинът принадлежат към една и съща организация в Shopify.',
  shopNotInstalled:
    'Приложението все още не е инсталирано в този магазин. В таблото за разработчици на Shopify отворете приложението, изберете „Инсталиране на приложението“, посочете този магазин и опитайте отново.',
  shopCredentials:
    'Shopify отхвърли идентификатора или тайната на клиента. Копирайте ги отново от настройките на приложението в таблото за разработчици на Shopify.',
  shopUnavailable: 'Shopify не може да бъде достигнат. Опитайте отново след малко.',
  mcpGenericError: 'Не успяхме да изпълним тази заявка. Опитайте отново.',
};
const cs: Copy = {
  dbSectionTitle: 'Databáze',
  dbSectionIntro:
    'Vyberte, kdo může používat jednotlivé databáze v Chatech a Aplikacích. Toto nastavení můžete kdykoli změnit.',
  dbAccessOff: 'Přístup je vypnutý',
  dbAccessEnabled: 'Přístup je povolen pro {count} z {total}',
  dbLoading: 'Načítání databází…',
  dbLoadError: 'Databáze se nepodařilo načíst. Zkontrolujte připojení a zkuste to znovu.',
  queryLimitLabel: 'Nejdelší doba dotazu',
  queryLimitHint:
    'Jak dlouho může jedno čtení běžet, než se zastaví. Chaty a Aplikace mohou požádat až o tuto dobu; jinak se čtení zastaví po {default}.',
  queryLimitDefault: 'Výchozí ({value})',
  queryLimitSource: 'Nastavení připojení ({value})',
  queryLimitSaving: 'Ukládání…',
  queryLimitSaved: 'Uloženo',
  queryLimitError: 'Časový limit se nepodařilo uložit. Zkuste to znovu.',
  queryLimitLoadError: 'Časový limit se nepodařilo načíst.',
  queryLimitRetry: 'Zkusit znovu',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'Ověření TLS selhalo. Zkontrolujte certifikát serveru nebo v části Pokročilé TLS přidejte certifikační autoritu.',
  pgAuthFailed:
    'PostgreSQL odmítl tyto přihlašovací údaje. Zkontrolujte uživatelské jméno a heslo.',
  pgNetworkFailed:
    'Poneva se k tomuto hostiteli nemohla připojit. Zkontrolujte název hostitele, port a firewall.',
  pgReadOnlyRequired:
    'Použijte repliku pouze pro čtení nebo vyhrazený databázový účet bez oprávnění správce, vytváření schémat a přepínání rolí.',
  pgNoTables:
    'Nebyly nalezeny žádné tabulky, které lze číst. Vyberte databázi s alespoň jednou tabulkou.',
  pgCatalogLarge:
    'Katalog této databáze je příliš velký. Před dalším pokusem zúžte databázi nebo schéma.',
  pgCatalogChanged: 'Katalog databáze se změnil. Připojení znovu otestujte.',
  pgUnsafeHost: 'Tento hostitel není povolen. Použijte veřejný koncový bod PostgreSQL s TLS.',
  pgSetupBusy: 'Jiné nastavení PostgreSQL již probíhá. Zkuste to znovu za chvíli.',
  invalidRequest: 'Zkontrolujte pole a zkuste to znovu.',
  connectionChanged: 'Toto připojení bylo změněno jinde. Znovu je načtěte a zkuste to znovu.',
  genericStepError: 'Tento krok se nepodařilo dokončit. Zkuste to znovu.',
  authNone: 'Bez přihlášení',
  authBearer: 'Přístupový klíč',
  authHeaders: 'Vlastní hlavičky',
  authBasic: 'Uživatelské jméno a heslo',
  authOauth: 'Přihlásit se pomocí služby',
  authShopify: 'Vývojářská aplikace Shopify',
  transportBuiltin: 'Spouští Poneva',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Legacy SSE',
  transportAuto: 'Automaticky rozpoznáno',
  statusNeedsAuth: 'Vyžaduje přihlášení',
  statusUnavailable: 'Nedostupné',
  statusConnected: 'Připojeno',
  mcpAuthRequired: 'Služba odmítla tyto přihlašovací údaje. Zkontrolujte je a zkuste to znovu.',
  mcpUrlBlocked:
    'Použijte veřejnou webovou adresu začínající na https://. Adresy v privátní síti nejsou podporovány.',
  mcpTimeout: 'Službě trvalo příliš dlouho, než odpověděla. Zkontrolujte adresu a zkuste to znovu.',
  mcpConnectionFailed: 'Nelze se připojit. Zkontrolujte webovou adresu a přihlašovací údaje.',
  mcpAuthorizationRequired: 'Pro opětovné připojení této služby se znovu přihlaste.',
  oauthNotConfigured:
    'Přihlášení v prohlížeči není v této instalaci Poneva nakonfigurováno. Obraťte se na správce.',
  mcpInvalidRequest: 'Zkontrolujte webovou adresu a vyplňte údaje pro vybraný způsob přihlášení.',
  shopInvalid: 'Zadejte adresu svého obchodu myshopify.com, například your-store.myshopify.com.',
  shopNotFound: 'Shopify na této adrese myshopify.com nemá žádný obchod.',
  shopNotPermitted:
    'Tuto aplikaci nelze v tomto obchodě použít. Nainstalujte aplikaci do obchodu z Shopify Dev Dashboard a ověřte, že aplikace i obchod patří stejné organizaci Shopify.',
  shopNotInstalled:
    'Aplikace zatím není v tomto obchodě nainstalována. V Shopify Dev Dashboard otevřete aplikaci, zvolte Install app, vyberte tento obchod a zkuste to znovu.',
  shopCredentials:
    'Shopify odmítl ID klienta nebo tajný klíč. Zkopírujte obě hodnoty znovu z nastavení aplikace v Shopify Dev Dashboard.',
  shopUnavailable: 'Shopify se nepodařilo kontaktovat. Zkuste to znovu za chvíli.',
  mcpGenericError: 'Tento požadavek se nepodařilo dokončit. Zkuste to znovu.',
};
const da: Copy = {
  dbSectionTitle: 'Databaser',
  dbSectionIntro:
    'Vælg, hvem der må bruge hver database i Chats og Apps. Du kan altid ændre det her.',
  dbAccessOff: 'Adgang er deaktiveret',
  dbAccessEnabled: 'Adgang aktiveret for {count} af {total}',
  dbLoading: 'Indlæser databaser…',
  dbLoadError: 'Vi kunne ikke indlæse dine databaser. Kontrollér din forbindelse, og prøv igen.',
  queryLimitLabel: 'Maksimal forespørgselstid',
  queryLimitHint:
    'Hvor længe en læseforespørgsel må køre, før den stoppes. Chats og Apps kan anmode om op til denne tid; ellers stoppes en læseforespørgsel efter {default}.',
  queryLimitDefault: 'Standard ({value})',
  queryLimitSource: 'Forbindelsesindstilling ({value})',
  queryLimitSaving: 'Gemmer…',
  queryLimitSaved: 'Gemt',
  queryLimitError: 'Vi kunne ikke gemme tidsgrænsen. Prøv igen.',
  queryLimitLoadError: 'Vi kunne ikke indlæse tidsgrænsen.',
  queryLimitRetry: 'Prøv igen',
  seconds: '{count} sek.',
  minutes: '{count} min.',
  pgTlsFailed:
    'TLS-verificering mislykkedes. Kontrollér servercertifikatet, eller tilføj dets CA-certifikat under Avanceret TLS.',
  pgAuthFailed:
    'PostgreSQL afviste disse legitimationsoplysninger. Kontrollér brugernavn og adgangskode.',
  pgNetworkFailed:
    'Poneva kunne ikke oprette forbindelse til denne vært. Kontrollér værtsnavn, port og firewall.',
  pgReadOnlyRequired:
    'Brug en skrivebeskyttet replika eller en dedikeret databasekonto uden tilladelser til administration, oprettelse af skemaer eller skift af roller.',
  pgNoTables: 'Der blev ikke fundet nogen læsbare tabeller. Vælg en database med mindst én tabel.',
  pgCatalogLarge:
    'Dette databasekatalog er for stort. Begræns databasen eller skemaet, før du prøver igen.',
  pgCatalogChanged: 'Databasekataloget er ændret. Test forbindelsen igen.',
  pgUnsafeHost: 'Denne vært er ikke tilladt. Brug et offentligt TLS PostgreSQL-slutpunkt.',
  pgSetupBusy: 'En anden PostgreSQL-konfiguration er allerede i gang. Prøv igen om et øjeblik.',
  invalidRequest: 'Kontrollér felterne, og prøv igen.',
  connectionChanged:
    'Denne forbindelse er blevet ændret et andet sted. Genindlæs den, og prøv igen.',
  genericStepError: 'Vi kunne ikke gennemføre dette trin. Prøv igen.',
  authNone: 'Ingen login',
  authBearer: 'Adgangsnøgle',
  authHeaders: 'Brugerdefinerede headere',
  authBasic: 'Brugernavn og adgangskode',
  authOauth: 'Log ind med tjenesten',
  authShopify: 'Shopify Developer App',
  transportBuiltin: 'Køres af Poneva',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Ældre SSE',
  transportAuto: 'Registreret automatisk',
  statusNeedsAuth: 'Login påkrævet',
  statusUnavailable: 'Ikke tilgængelig',
  statusConnected: 'Forbundet',
  mcpAuthRequired: 'Tjenesten afviste disse loginoplysninger. Kontrollér dem, og prøv igen.',
  mcpUrlBlocked:
    'Brug en offentlig webadresse, der begynder med https://. Adresser på private netværk understøttes ikke.',
  mcpTimeout: 'Tjenesten brugte for lang tid på at svare. Kontrollér adressen, og prøv igen.',
  mcpConnectionFailed:
    'Vi kunne ikke oprette forbindelse. Kontrollér webadressen og loginoplysningerne.',
  mcpAuthorizationRequired: 'Log ind igen for at genoprette forbindelsen til denne tjeneste.',
  oauthNotConfigured:
    'Login via browseren er ikke konfigureret på denne Poneva-installation. Kontakt din administrator.',
  mcpInvalidRequest: 'Kontrollér webadressen, og udfyld oplysningerne for den valgte loginmetode.',
  shopInvalid: 'Indtast din butiks myshopify.com-adresse, f.eks. your-store.myshopify.com.',
  shopNotFound: 'Shopify har ingen butik på denne myshopify.com-adresse.',
  shopNotPermitted:
    'Denne app kan ikke bruges i den pågældende butik. Installér appen i butikken fra Shopifys Dev Dashboard, og sørg for, at appen og butikken tilhører den samme Shopify-organisation.',
  shopNotInstalled:
    'Appen er endnu ikke installeret i denne butik. Åbn appen i Shopifys Dev Dashboard, vælg Install app, vælg denne butik, og prøv derefter igen.',
  shopCredentials:
    "Shopify afviste klient-id'et eller den hemmelige nøgle. Kopiér begge igen fra appens indstillinger i Shopifys Dev Dashboard.",
  shopUnavailable: 'Der kunne ikke oprettes forbindelse til Shopify. Prøv igen om et øjeblik.',
  mcpGenericError: 'Vi kunne ikke gennemføre denne anmodning. Prøv igen.',
};
const de: Copy = {
  dbSectionTitle: 'Datenbanken',
  dbSectionIntro:
    'Wählen Sie aus, wer die einzelnen Datenbanken in Chats und Apps verwenden darf. Sie können dies jederzeit hier ändern.',
  dbAccessOff: 'Zugriff deaktiviert',
  dbAccessEnabled: 'Zugriff für {count} von {total} aktiviert',
  dbLoading: 'Datenbanken werden geladen…',
  dbLoadError:
    'Ihre Datenbanken konnten nicht geladen werden. Überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
  queryLimitLabel: 'Maximale Abfragedauer',
  queryLimitHint:
    'Legt fest, wie lange eine einzelne Leseabfrage laufen darf, bevor sie beendet wird. Chats und Apps können Abfragen bis zu diesem Wert stellen; andernfalls wird eine Leseabfrage nach {default} beendet.',
  queryLimitDefault: 'Standard ({value})',
  queryLimitSource: 'Verbindungseinstellung ({value})',
  queryLimitSaving: 'Wird gespeichert…',
  queryLimitSaved: 'Gespeichert',
  queryLimitError: 'Das Zeitlimit konnte nicht gespeichert werden. Versuchen Sie es erneut.',
  queryLimitLoadError: 'Das Zeitlimit konnte nicht geladen werden.',
  queryLimitRetry: 'Erneut versuchen',
  seconds: '{count} Sek.',
  minutes: '{count} Min.',
  pgTlsFailed:
    'Die TLS-Verifizierung ist fehlgeschlagen. Überprüfen Sie das Serverzertifikat oder fügen Sie das zugehörige CA-Zertifikat unter „Erweitertes TLS“ hinzu.',
  pgAuthFailed:
    'PostgreSQL hat diese Anmeldedaten abgelehnt. Überprüfen Sie Benutzername und Passwort.',
  pgNetworkFailed:
    'Poneva konnte diesen Host nicht erreichen. Überprüfen Sie Hostname, Port und Firewall.',
  pgReadOnlyRequired:
    'Verwenden Sie ein schreibgeschütztes Replikat oder ein dediziertes Datenbankkonto ohne Berechtigungen für die Administration, Schemaerstellung oder den Rollenwechsel.',
  pgNoTables:
    'Es wurden keine lesbaren Tabellen gefunden. Wählen Sie eine Datenbank mit mindestens einer Tabelle aus.',
  pgCatalogLarge:
    'Der Katalog dieser Datenbank ist zu groß. Grenzen Sie die Datenbank oder das Schema ein, bevor Sie es erneut versuchen.',
  pgCatalogChanged: 'Der Datenbankkatalog wurde geändert. Testen Sie die Verbindung erneut.',
  pgUnsafeHost:
    'Dieser Host ist nicht zulässig. Verwenden Sie einen öffentlich erreichbaren TLS-PostgreSQL-Endpunkt.',
  pgSetupBusy:
    'Eine andere PostgreSQL-Einrichtung läuft bereits. Versuchen Sie es gleich noch einmal.',
  invalidRequest: 'Überprüfen Sie die Felder und versuchen Sie es erneut.',
  connectionChanged:
    'Diese Verbindung wurde an anderer Stelle geändert. Laden Sie sie neu und versuchen Sie es erneut.',
  genericStepError: 'Dieser Schritt konnte nicht abgeschlossen werden. Versuchen Sie es erneut.',
  authNone: 'Keine Anmeldung',
  authBearer: 'Zugriffsschlüssel',
  authHeaders: 'Benutzerdefinierte Header',
  authBasic: 'Benutzername und Passwort',
  authOauth: 'Beim Dienst anmelden',
  authShopify: 'Shopify-Entwickler-App',
  transportBuiltin: 'Von Poneva ausgeführt',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Legacy-SSE',
  transportAuto: 'Automatisch erkannt',
  statusNeedsAuth: 'Anmeldung erforderlich',
  statusUnavailable: 'Nicht verfügbar',
  statusConnected: 'Verbunden',
  mcpAuthRequired:
    'Der Dienst hat diese Anmeldedaten abgelehnt. Überprüfen Sie sie und versuchen Sie es erneut.',
  mcpUrlBlocked:
    'Verwenden Sie eine öffentliche Webadresse, die mit https:// beginnt. Adressen privater Netzwerke werden nicht unterstützt.',
  mcpTimeout:
    'Der Dienst hat zu lange für eine Antwort gebraucht. Überprüfen Sie die Adresse und versuchen Sie es erneut.',
  mcpConnectionFailed:
    'Die Verbindung konnte nicht hergestellt werden. Überprüfen Sie die Webadresse und die Anmeldedaten.',
  mcpAuthorizationRequired: 'Melden Sie sich erneut an, um diesen Dienst wieder zu verbinden.',
  oauthNotConfigured:
    'Die Browseranmeldung ist in dieser Poneva-Installation nicht konfiguriert. Wenden Sie sich an Ihre Administration.',
  mcpInvalidRequest:
    'Überprüfen Sie die Webadresse und geben Sie die Angaben für die ausgewählte Anmeldemethode ein.',
  shopInvalid:
    'Geben Sie die myshopify.com-Adresse Ihres Shops ein, zum Beispiel ihr-shop.myshopify.com.',
  shopNotFound: 'Shopify hat keinen Shop unter dieser myshopify.com-Adresse gefunden.',
  shopNotPermitted:
    'Diese App kann in diesem Shop nicht verwendet werden. Installieren Sie die App über das Shopify Dev Dashboard in diesem Shop und stellen Sie sicher, dass App und Shop derselben Shopify-Organisation angehören.',
  shopNotInstalled:
    'Die App ist noch nicht in diesem Shop installiert. Öffnen Sie im Shopify Dev Dashboard die App, wählen Sie „App installieren“ und anschließend diesen Shop aus. Versuchen Sie es dann erneut.',
  shopCredentials:
    'Shopify hat die Client-ID oder das Secret abgelehnt. Kopieren Sie beide erneut aus den App-Einstellungen im Shopify Dev Dashboard.',
  shopUnavailable: 'Shopify konnte nicht erreicht werden. Versuchen Sie es gleich noch einmal.',
  mcpGenericError: 'Diese Anfrage konnte nicht abgeschlossen werden. Versuchen Sie es erneut.',
};
const el: Copy = {
  dbSectionTitle: 'Βάσεις δεδομένων',
  dbSectionIntro:
    'Επιλέξτε ποιοι μπορούν να χρησιμοποιούν κάθε βάση δεδομένων στις Συνομιλίες και τις Εφαρμογές. Μπορείτε να το αλλάξετε εδώ οποιαδήποτε στιγμή.',
  dbAccessOff: 'Η πρόσβαση είναι απενεργοποιημένη',
  dbAccessEnabled: 'Η πρόσβαση είναι ενεργοποιημένη για {count} από {total}',
  dbLoading: 'Φόρτωση βάσεων δεδομένων…',
  dbLoadError:
    'Δεν ήταν δυνατή η φόρτωση των βάσεων δεδομένων σας. Ελέγξτε τη σύνδεσή σας και δοκιμάστε ξανά.',
  queryLimitLabel: 'Μέγιστος χρόνος ερωτήματος',
  queryLimitHint:
    'Για πόσο μπορεί να εκτελείται μια ανάγνωση πριν διακοπεί. Οι Συνομιλίες και οι Εφαρμογές μπορούν να ζητήσουν έως αυτό το όριο· διαφορετικά, η ανάγνωση διακόπτεται μετά από {default}.',
  queryLimitDefault: 'Προεπιλογή ({value})',
  queryLimitSource: 'Ρύθμιση σύνδεσης ({value})',
  queryLimitSaving: 'Αποθήκευση…',
  queryLimitSaved: 'Αποθηκεύτηκε',
  queryLimitError: 'Δεν ήταν δυνατή η αποθήκευση του χρονικού ορίου. Δοκιμάστε ξανά.',
  queryLimitLoadError: 'Δεν ήταν δυνατή η φόρτωση του χρονικού ορίου.',
  queryLimitRetry: 'Δοκιμάστε ξανά',
  seconds: '{count} δευτ.',
  minutes: '{count} λεπ.',
  pgTlsFailed:
    'Η επαλήθευση TLS απέτυχε. Ελέγξτε το πιστοποιητικό του διακομιστή ή προσθέστε το πιστοποιητικό CA στις Ρυθμίσεις TLS για προχωρημένους.',
  pgAuthFailed:
    'Το PostgreSQL απέρριψε αυτά τα διαπιστευτήρια. Ελέγξτε το όνομα χρήστη και τον κωδικό πρόσβασης.',
  pgNetworkFailed:
    'Το Poneva δεν μπόρεσε να συνδεθεί σε αυτόν τον κεντρικό υπολογιστή. Ελέγξτε το όνομα κεντρικού υπολογιστή, τη θύρα και το τείχος προστασίας.',
  pgReadOnlyRequired:
    'Χρησιμοποιήστε ένα αντίγραφο μόνο για ανάγνωση ή έναν αποκλειστικό λογαριασμό βάσης δεδομένων χωρίς δικαιώματα διαχειριστή, δημιουργίας σχημάτων ή εναλλαγής ρόλων.',
  pgNoTables:
    'Δεν βρέθηκαν αναγνώσιμοι πίνακες. Επιλέξτε μια βάση δεδομένων με τουλάχιστον έναν πίνακα.',
  pgCatalogLarge:
    'Ο κατάλογος αυτής της βάσης δεδομένων είναι υπερβολικά μεγάλος. Περιορίστε τη βάση δεδομένων ή το σχήμα πριν δοκιμάσετε ξανά.',
  pgCatalogChanged: 'Ο κατάλογος της βάσης δεδομένων άλλαξε. Δοκιμάστε ξανά τη σύνδεση.',
  pgUnsafeHost:
    'Αυτός ο κεντρικός υπολογιστής δεν επιτρέπεται. Χρησιμοποιήστε ένα δημόσιο τελικό σημείο PostgreSQL με TLS.',
  pgSetupBusy: 'Μια άλλη ρύθμιση PostgreSQL βρίσκεται ήδη σε εξέλιξη. Δοκιμάστε ξανά σε λίγο.',
  invalidRequest: 'Ελέγξτε τα πεδία και δοκιμάστε ξανά.',
  connectionChanged: 'Αυτή η σύνδεση άλλαξε αλλού. Φορτώστε την ξανά και δοκιμάστε ξανά.',
  genericStepError: 'Δεν ήταν δυνατή η ολοκλήρωση αυτού του βήματος. Δοκιμάστε ξανά.',
  authNone: 'Χωρίς σύνδεση',
  authBearer: 'Κλειδί πρόσβασης',
  authHeaders: 'Προσαρμοσμένες κεφαλίδες',
  authBasic: 'Όνομα χρήστη και κωδικός πρόσβασης',
  authOauth: 'Σύνδεση με την υπηρεσία',
  authShopify: 'Εφαρμογή προγραμματιστή Shopify',
  transportBuiltin: 'Εκτελείται από το Poneva',
  transportHttp: 'HTTP με δυνατότητα ροής',
  transportSse: 'Παλαιό SSE',
  transportAuto: 'Αυτόματος εντοπισμός',
  statusNeedsAuth: 'Απαιτείται σύνδεση',
  statusUnavailable: 'Μη διαθέσιμο',
  statusConnected: 'Συνδεδεμένο',
  mcpAuthRequired: 'Η υπηρεσία απέρριψε αυτά τα στοιχεία σύνδεσης. Ελέγξτε τα και δοκιμάστε ξανά.',
  mcpUrlBlocked:
    'Χρησιμοποιήστε μια δημόσια διεύθυνση ιστού που ξεκινά με https://. Οι διευθύνσεις ιδιωτικών δικτύων δεν υποστηρίζονται.',
  mcpTimeout: 'Η υπηρεσία άργησε υπερβολικά να απαντήσει. Ελέγξτε τη διεύθυνση και δοκιμάστε ξανά.',
  mcpConnectionFailed:
    'Δεν ήταν δυνατή η σύνδεση. Ελέγξτε τη διεύθυνση ιστού και τα στοιχεία σύνδεσης.',
  mcpAuthorizationRequired: 'Συνδεθείτε ξανά για να επανασυνδέσετε αυτήν την υπηρεσία.',
  oauthNotConfigured:
    'Η σύνδεση μέσω προγράμματος περιήγησης δεν έχει ρυθμιστεί σε αυτήν την εγκατάσταση του Poneva. Επικοινωνήστε με τον διαχειριστή σας.',
  mcpInvalidRequest:
    'Ελέγξτε τη διεύθυνση ιστού και συμπληρώστε τα στοιχεία για την επιλεγμένη μέθοδο σύνδεσης.',
  shopInvalid:
    'Εισαγάγετε τη διεύθυνση myshopify.com του καταστήματός σας, για παράδειγμα your-store.myshopify.com.',
  shopNotFound: 'Η Shopify δεν έχει κατάστημα σε αυτήν τη διεύθυνση myshopify.com.',
  shopNotPermitted:
    'Αυτή η εφαρμογή δεν μπορεί να χρησιμοποιηθεί σε αυτό το κατάστημα. Εγκαταστήστε την εφαρμογή στο κατάστημα από το Shopify Dev Dashboard και βεβαιωθείτε ότι η εφαρμογή και το κατάστημα ανήκουν στον ίδιο οργανισμό Shopify.',
  shopNotInstalled:
    'Η εφαρμογή δεν έχει εγκατασταθεί ακόμη σε αυτό το κατάστημα. Στο Shopify Dev Dashboard, ανοίξτε την εφαρμογή, επιλέξτε Install app και επιλέξτε αυτό το κατάστημα. Στη συνέχεια δοκιμάστε ξανά.',
  shopCredentials:
    'Η Shopify απέρριψε το αναγνωριστικό ή το μυστικό του πελάτη. Αντιγράψτε ξανά και τα δύο από τις ρυθμίσεις της εφαρμογής στο Shopify Dev Dashboard.',
  shopUnavailable: 'Δεν ήταν δυνατή η σύνδεση με τη Shopify. Δοκιμάστε ξανά σε λίγο.',
  mcpGenericError: 'Δεν ήταν δυνατή η ολοκλήρωση αυτού του αιτήματος. Δοκιμάστε ξανά.',
};
const es: Copy = {
  dbSectionTitle: 'Bases de datos',
  dbSectionIntro:
    'Elige quién puede usar cada base de datos en Chats y Aplicaciones. Puedes cambiarlo aquí en cualquier momento.',
  dbAccessOff: 'Acceso desactivado',
  dbAccessEnabled: 'Acceso habilitado para {count} de {total}',
  dbLoading: 'Cargando bases de datos…',
  dbLoadError: 'No pudimos cargar tus bases de datos. Comprueba tu conexión e inténtalo de nuevo.',
  queryLimitLabel: 'Tiempo máximo de consulta',
  queryLimitHint:
    'Cuánto tiempo puede ejecutarse una lectura antes de detenerse. Los Chats y las Aplicaciones pueden solicitar hasta este límite; de lo contrario, la lectura se detiene después de {default}.',
  queryLimitDefault: 'Predeterminado ({value})',
  queryLimitSource: 'Configuración de la conexión ({value})',
  queryLimitSaving: 'Guardando…',
  queryLimitSaved: 'Guardado',
  queryLimitError: 'No pudimos guardar el límite de tiempo. Inténtalo de nuevo.',
  queryLimitLoadError: 'No pudimos cargar el límite de tiempo.',
  queryLimitRetry: 'Intentar de nuevo',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'La verificación de TLS falló. Comprueba el certificado del servidor o añade su certificado de CA en TLS avanzado.',
  pgAuthFailed:
    'PostgreSQL rechazó estas credenciales. Comprueba el nombre de usuario y la contraseña.',
  pgNetworkFailed:
    'Poneva no pudo conectarse a este host. Comprueba el nombre de host, el puerto y el firewall.',
  pgReadOnlyRequired:
    'Usa una réplica de solo lectura o una cuenta de base de datos específica sin permisos de administrador, creación de esquemas ni cambio de roles.',
  pgNoTables: 'No se encontraron tablas legibles. Elige una base de datos con al menos una tabla.',
  pgCatalogLarge:
    'El catálogo de esta base de datos es demasiado grande. Reduce el alcance de la base de datos o del esquema antes de volver a intentarlo.',
  pgCatalogChanged: 'El catálogo de la base de datos cambió. Vuelve a probar la conexión.',
  pgUnsafeHost: 'Este host no está permitido. Usa un endpoint público de PostgreSQL con TLS.',
  pgSetupBusy:
    'Ya hay otra configuración de PostgreSQL en curso. Inténtalo de nuevo en un momento.',
  invalidRequest: 'Comprueba los campos e inténtalo de nuevo.',
  connectionChanged: 'Esta conexión cambió en otro lugar. Vuelve a cargarla e inténtalo de nuevo.',
  genericStepError: 'No pudimos completar este paso. Inténtalo de nuevo.',
  authNone: 'Sin inicio de sesión',
  authBearer: 'Clave de acceso',
  authHeaders: 'Encabezados personalizados',
  authBasic: 'Nombre de usuario y contraseña',
  authOauth: 'Iniciar sesión con el servicio',
  authShopify: 'Aplicación de desarrollador de Shopify',
  transportBuiltin: 'Ejecutado por Poneva',
  transportHttp: 'HTTP transmitible',
  transportSse: 'SSE heredado',
  transportAuto: 'Detectado automáticamente',
  statusNeedsAuth: 'Requiere inicio de sesión',
  statusUnavailable: 'No disponible',
  statusConnected: 'Conectado',
  mcpAuthRequired:
    'El servicio rechazó estos datos de inicio de sesión. Compruébalos e inténtalo de nuevo.',
  mcpUrlBlocked:
    'Usa una dirección web pública que empiece por https://. No se admiten direcciones de redes privadas.',
  mcpTimeout:
    'El servicio tardó demasiado en responder. Comprueba la dirección e inténtalo de nuevo.',
  mcpConnectionFailed:
    'No pudimos conectarnos. Comprueba la dirección web y los datos de inicio de sesión.',
  mcpAuthorizationRequired: 'Vuelve a iniciar sesión para reconectar este servicio.',
  oauthNotConfigured:
    'El inicio de sesión en el navegador no está configurado en esta instalación de Poneva. Ponte en contacto con tu administrador.',
  mcpInvalidRequest:
    'Comprueba la dirección web y completa los datos del método de inicio de sesión seleccionado.',
  shopInvalid:
    'Introduce la dirección myshopify.com de tu tienda, por ejemplo, tu-tienda.myshopify.com.',
  shopNotFound: 'Shopify no tiene ninguna tienda en esa dirección myshopify.com.',
  shopNotPermitted:
    'Esta aplicación no puede usarse en esa tienda. Instala la aplicación en la tienda desde el panel de desarrolladores de Shopify y asegúrate de que la aplicación y la tienda pertenezcan a la misma organización de Shopify.',
  shopNotInstalled:
    'La aplicación todavía no está instalada en esta tienda. En el panel de desarrolladores de Shopify, abre la aplicación, selecciona Instalar aplicación y elige esta tienda; después, inténtalo de nuevo.',
  shopCredentials:
    'Shopify rechazó el ID de cliente o el secreto. Vuelve a copiar ambos desde la configuración de la aplicación en el panel de desarrolladores de Shopify.',
  shopUnavailable: 'No se pudo acceder a Shopify. Inténtalo de nuevo en un momento.',
  mcpGenericError: 'No pudimos completar esa solicitud. Inténtalo de nuevo.',
};
const et: Copy = {
  dbSectionTitle: 'Andmebaasid',
  dbSectionIntro:
    'Valige, kes saab iga andmebaasi vestlustes ja rakendustes kasutada. Saate seda siin igal ajal muuta.',
  dbAccessOff: 'Juurdepääs on välja lülitatud',
  dbAccessEnabled: 'Juurdepääs on lubatud: {count}/{total}',
  dbLoading: 'Andmebaaside laadimine…',
  dbLoadError: 'Teie andmebaaside laadimine ebaõnnestus. Kontrollige ühendust ja proovige uuesti.',
  queryLimitLabel: 'Päringu maksimaalne kestus',
  queryLimitHint:
    'Kui kaua võib üks lugemispäring töötada, enne kui see peatatakse. Vestlused ja rakendused võivad selle ajani päringuid teha; vastasel juhul peatatakse lugemispäring pärast {default}.',
  queryLimitDefault: 'Vaikeseade ({value})',
  queryLimitSource: 'Ühenduse seade ({value})',
  queryLimitSaving: 'Salvestamine…',
  queryLimitSaved: 'Salvestatud',
  queryLimitError: 'Ajalimiidi salvestamine ebaõnnestus. Proovige uuesti.',
  queryLimitLoadError: 'Ajalimiidi laadimine ebaõnnestus.',
  queryLimitRetry: 'Proovi uuesti',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'TLS-i kontrollimine ebaõnnestus. Kontrollige serveri sertifikaati või lisage selle CA-sertifikaat jaotises Täpsem TLS.',
  pgAuthFailed: 'PostgreSQL lükkas need mandaadid tagasi. Kontrollige kasutajanime ja parooli.',
  pgNetworkFailed:
    'Poneva ei saanud selle hostiga ühendust. Kontrollige hostinime, porti ja tulemüüri.',
  pgReadOnlyRequired:
    'Kasutage kirjutuskaitstud koopiat või spetsiaalset andmebaasikontot, millel puuduvad administraatori, skeemi loomise ja rolli vahetamise õigused.',
  pgNoTables: 'Loetavaid tabeleid ei leitud. Valige andmebaas, milles on vähemalt üks tabel.',
  pgCatalogLarge:
    'Selle andmebaasi kataloog on liiga suur. Enne uut katset vähendage andmebaasi või skeemi ulatust.',
  pgCatalogChanged: 'Andmebaasi kataloog on muutunud. Testige ühendust uuesti.',
  pgUnsafeHost: 'See host pole lubatud. Kasutage avalikku TLS-iga PostgreSQL-i lõpp-punkti.',
  pgSetupBusy: 'Teine PostgreSQL-i seadistus on juba käimas. Proovige hetke pärast uuesti.',
  invalidRequest: 'Kontrollige välju ja proovige uuesti.',
  connectionChanged: 'Seda ühendust muudeti mujal. Laadige see uuesti ja proovige uuesti.',
  genericStepError: 'Seda toimingut ei saanud lõpule viia. Proovige uuesti.',
  authNone: 'Sisselogimine puudub',
  authBearer: 'Juurdepääsuvõti',
  authHeaders: 'Kohandatud päised',
  authBasic: 'Kasutajanimi ja parool',
  authOauth: 'Logi teenusesse sisse',
  authShopify: 'Shopify arendajarakendus',
  transportBuiltin: 'Käitab Poneva',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Legacy SSE',
  transportAuto: 'Automaatselt tuvastatud',
  statusNeedsAuth: 'Vajab sisselogimist',
  statusUnavailable: 'Pole saadaval',
  statusConnected: 'Ühendatud',
  mcpAuthRequired:
    'Teenus lükkas need sisselogimisandmed tagasi. Kontrollige neid ja proovige uuesti.',
  mcpUrlBlocked:
    'Kasutage avalikku veebiaadressi, mis algab tekstiga https://. Privaatvõrgu aadresse ei toetata.',
  mcpTimeout: 'Teenuse vastamine võttis liiga kaua aega. Kontrollige aadressi ja proovige uuesti.',
  mcpConnectionFailed:
    'Ühenduse loomine ebaõnnestus. Kontrollige veebiaadressi ja sisselogimisandmeid.',
  mcpAuthorizationRequired: 'Selle teenuse taasühendamiseks logige uuesti sisse.',
  oauthNotConfigured:
    'Brauseriga sisselogimine pole selles Poneva installatsioonis seadistatud. Võtke ühendust administraatoriga.',
  mcpInvalidRequest: 'Kontrollige veebiaadressi ja täitke valitud sisselogimismeetodi üksikasjad.',
  shopInvalid: 'Sisestage oma poe myshopify.com-aadress, näiteks your-store.myshopify.com.',
  shopNotFound: 'Shopify ei leidnud selle myshopify.com-aadressi juurest poodi.',
  shopNotPermitted:
    'Seda rakendust ei saa selles poes kasutada. Installige rakendus Shopify Dev Dashboardis sellesse poodi ning veenduge, et rakendus ja pood kuuluvad samasse Shopify organisatsiooni.',
  shopNotInstalled:
    'Rakendus pole sellesse poodi veel installitud. Avage Shopify Dev Dashboardis rakendus, valige Install app, määrake see pood ja proovige uuesti.',
  shopCredentials:
    'Shopify lükkas kliendi ID või saladuse tagasi. Kopeerige mõlemad Shopify Dev Dashboardis rakenduse seadetest uuesti.',
  shopUnavailable: 'Shopifyga ei saanud ühendust. Proovige hetke pärast uuesti.',
  mcpGenericError: 'Seda päringut ei saanud lõpule viia. Proovige uuesti.',
};
const fi: Copy = {
  dbSectionTitle: 'Tietokannat',
  dbSectionIntro:
    'Valitse, ketkä voivat käyttää kutakin tietokantaa keskusteluissa ja sovelluksissa. Voit muuttaa tätä milloin tahansa täällä.',
  dbAccessOff: 'Käyttöoikeus ei ole käytössä',
  dbAccessEnabled: 'Käyttöoikeus käytössä: {count}/{total}',
  dbLoading: 'Ladataan tietokantoja…',
  dbLoadError: 'Tietokantojen lataaminen epäonnistui. Tarkista yhteytesi ja yritä uudelleen.',
  queryLimitLabel: 'Pisin kyselyaika',
  queryLimitHint:
    'Kuinka kauan yksi lukupyyntö voi kestää ennen sen keskeyttämistä. Keskustelut ja sovellukset voivat pyytää enintään tätä aikaa; muuten luku keskeytetään {default} kuluttua.',
  queryLimitDefault: 'Oletus ({value})',
  queryLimitSource: 'Yhteysasetus ({value})',
  queryLimitSaving: 'Tallennetaan…',
  queryLimitSaved: 'Tallennettu',
  queryLimitError: 'Aikarajan tallentaminen epäonnistui. Yritä uudelleen.',
  queryLimitLoadError: 'Aikarajan lataaminen epäonnistui.',
  queryLimitRetry: 'Yritä uudelleen',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'TLS-varmennus epäonnistui. Tarkista palvelimen varmenne tai lisää sen CA-varmenne TLS-lisäasetuksissa.',
  pgAuthFailed: 'PostgreSQL hylkäsi nämä kirjautumistiedot. Tarkista käyttäjänimi ja salasana.',
  pgNetworkFailed:
    'Poneva ei saanut yhteyttä tähän palvelimeen. Tarkista isäntänimi, portti ja palomuuri.',
  pgReadOnlyRequired:
    'Käytä vain luku -replikaa tai erillistä tietokantatiliä, jolla ei ole järjestelmänvalvojan, skeeman luonnin tai roolin vaihtamisen oikeuksia.',
  pgNoTables: 'Luettavia tauluja ei löytynyt. Valitse tietokanta, jossa on vähintään yksi taulu.',
  pgCatalogLarge:
    'Tämän tietokannan luettelo on liian suuri. Rajaa tietokantaa tai skeemaa ennen uutta yritystä.',
  pgCatalogChanged: 'Tietokannan luettelo muuttui. Testaa yhteys uudelleen.',
  pgUnsafeHost:
    'Tämä isäntä ei ole sallittu. Käytä julkista TLS-suojattua PostgreSQL-päätepistettä.',
  pgSetupBusy: 'Toinen PostgreSQL-määritys on jo käynnissä. Yritä hetken kuluttua uudelleen.',
  invalidRequest: 'Tarkista kentät ja yritä uudelleen.',
  connectionChanged: 'Tätä yhteyttä muutettiin muualla. Lataa se uudelleen ja yritä uudelleen.',
  genericStepError: 'Tämän vaiheen suorittaminen epäonnistui. Yritä uudelleen.',
  authNone: 'Ei kirjautumista',
  authBearer: 'Käyttöavain',
  authHeaders: 'Mukautetut otsakkeet',
  authBasic: 'Käyttäjänimi ja salasana',
  authOauth: 'Kirjaudu palveluun',
  authShopify: 'Shopify Developer App',
  transportBuiltin: 'Ponen suorittama',
  transportHttp: 'Suoratoistettava HTTP',
  transportSse: 'Vanha SSE',
  transportAuto: 'Automaattisesti tunnistettu',
  statusNeedsAuth: 'Kirjautuminen tarvitaan',
  statusUnavailable: 'Ei käytettävissä',
  statusConnected: 'Yhdistetty',
  mcpAuthRequired: 'Palvelu hylkäsi nämä kirjautumistiedot. Tarkista ne ja yritä uudelleen.',
  mcpUrlBlocked:
    'Käytä julkista https://-alkuista verkko-osoitetta. Yksityisen verkon osoitteita ei tueta.',
  mcpTimeout: 'Palvelun vastaaminen kesti liian kauan. Tarkista osoite ja yritä uudelleen.',
  mcpConnectionFailed:
    'Yhteyden muodostaminen epäonnistui. Tarkista verkko-osoite ja kirjautumistiedot.',
  mcpAuthorizationRequired: 'Kirjaudu uudelleen yhdistääksesi tämän palvelun.',
  oauthNotConfigured:
    'Selainkirjautumista ei ole määritetty tähän Poneva-asennukseen. Ota yhteyttä järjestelmänvalvojaasi.',
  mcpInvalidRequest: 'Tarkista verkko-osoite ja täytä valitun kirjautumistavan tiedot.',
  shopInvalid: 'Anna kauppasi myshopify.com-osoite, esimerkiksi your-store.myshopify.com.',
  shopNotFound: 'Shopifylla ei ole kauppaa tässä myshopify.com-osoitteessa.',
  shopNotPermitted:
    'Tätä sovellusta ei voi käyttää tässä kaupassa. Asenna sovellus Shopify’s Dev Dashboardista tähän kauppaan ja varmista, että sovellus ja kauppa kuuluvat samaan Shopify-organisaatioon.',
  shopNotInstalled:
    'Sovellusta ei ole vielä asennettu tähän kauppaan. Avaa sovellus Shopify’s Dev Dashboardissa, valitse Install app ja valitse tämä kauppa. Yritä sitten uudelleen.',
  shopCredentials:
    'Shopify hylkäsi asiakastunnuksen tai salaisuuden. Kopioi molemmat uudelleen sovelluksen asetuksista Shopify’s Dev Dashboardissa.',
  shopUnavailable: 'Shopayhin ei saatu yhteyttä. Yritä hetken kuluttua uudelleen.',
  mcpGenericError: 'Pyynnön suorittaminen epäonnistui. Yritä uudelleen.',
};
const fr: Copy = {
  dbSectionTitle: 'Bases de données',
  dbSectionIntro:
    'Choisissez qui peut utiliser chaque base de données dans les discussions et les applications. Vous pouvez modifier ce réglage à tout moment.',
  dbAccessOff: 'Accès désactivé',
  dbAccessEnabled: 'Accès activé pour {count} sur {total}',
  dbLoading: 'Chargement des bases de données…',
  dbLoadError:
    'Nous n’avons pas pu charger vos bases de données. Vérifiez votre connexion et réessayez.',
  queryLimitLabel: 'Durée maximale d’une requête',
  queryLimitHint:
    'Durée maximale d’une lecture avant son arrêt. Les discussions et les applications peuvent demander jusqu’à cette durée ; sinon, une lecture s’arrête après {default}.',
  queryLimitDefault: 'Par défaut ({value})',
  queryLimitSource: 'Paramètre de connexion ({value})',
  queryLimitSaving: 'Enregistrement…',
  queryLimitSaved: 'Enregistré',
  queryLimitError: 'Nous n’avons pas pu enregistrer la limite de durée. Réessayez.',
  queryLimitLoadError: 'Nous n’avons pas pu charger la limite de durée.',
  queryLimitRetry: 'Réessayer',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'La vérification TLS a échoué. Vérifiez le certificat du serveur ou ajoutez son certificat d’autorité de certification dans TLS avancé.',
  pgAuthFailed:
    'PostgreSQL a refusé ces identifiants. Vérifiez le nom d’utilisateur et le mot de passe.',
  pgNetworkFailed:
    'Poneva n’a pas pu joindre cet hôte. Vérifiez le nom d’hôte, le port et le pare-feu.',
  pgReadOnlyRequired:
    'Utilisez une réplique en lecture seule ou un compte de base de données dédié sans autorisations d’administrateur, de création de schémas ou de changement de rôle.',
  pgNoTables:
    'Aucune table lisible n’a été trouvée. Choisissez une base de données contenant au moins une table.',
  pgCatalogLarge:
    'Le catalogue de cette base de données est trop volumineux. Réduisez la portée de la base de données ou du schéma avant de réessayer.',
  pgCatalogChanged: 'Le catalogue de la base de données a changé. Testez à nouveau la connexion.',
  pgUnsafeHost:
    'Cet hôte n’est pas autorisé. Utilisez un point de terminaison PostgreSQL public avec TLS.',
  pgSetupBusy: 'Une autre configuration PostgreSQL est déjà en cours. Réessayez dans un instant.',
  invalidRequest: 'Vérifiez les champs et réessayez.',
  connectionChanged: 'Cette connexion a été modifiée ailleurs. Rechargez-la et réessayez.',
  genericStepError: 'Nous n’avons pas pu terminer cette étape. Réessayez.',
  authNone: 'Aucune connexion',
  authBearer: 'Clé d’accès',
  authHeaders: 'En-têtes personnalisés',
  authBasic: 'Nom d’utilisateur et mot de passe',
  authOauth: 'Connexion avec le service',
  authShopify: 'Application de développement Shopify',
  transportBuiltin: 'Exécuté par Poneva',
  transportHttp: 'HTTP diffusé',
  transportSse: 'SSE historique',
  transportAuto: 'Détecté automatiquement',
  statusNeedsAuth: 'Connexion requise',
  statusUnavailable: 'Indisponible',
  statusConnected: 'Connecté',
  mcpAuthRequired: 'Le service a refusé ces informations de connexion. Vérifiez-les et réessayez.',
  mcpUrlBlocked:
    'Utilisez une adresse web publique commençant par https://. Les adresses de réseaux privés ne sont pas prises en charge.',
  mcpTimeout: 'Le service a mis trop de temps à répondre. Vérifiez l’adresse et réessayez.',
  mcpConnectionFailed:
    'Nous n’avons pas pu nous connecter. Vérifiez l’adresse web et les informations de connexion.',
  mcpAuthorizationRequired: 'Reconnectez-vous pour reconnecter ce service.',
  oauthNotConfigured:
    'La connexion dans le navigateur n’est pas configurée sur cette installation de Poneva. Contactez votre administrateur.',
  mcpInvalidRequest:
    'Vérifiez l’adresse web et renseignez les informations correspondant à la méthode de connexion sélectionnée.',
  shopInvalid:
    'Saisissez l’adresse myshopify.com de votre boutique, par exemple votre-boutique.myshopify.com.',
  shopNotFound: 'Shopify ne possède aucune boutique à cette adresse myshopify.com.',
  shopNotPermitted:
    'Cette application ne peut pas être utilisée sur cette boutique. Installez l’application sur la boutique depuis le Shopify Dev Dashboard et vérifiez que l’application et la boutique appartiennent à la même organisation Shopify.',
  shopNotInstalled:
    'L’application n’est pas encore installée sur cette boutique. Dans le Shopify Dev Dashboard, ouvrez l’application, choisissez Install app, sélectionnez cette boutique, puis réessayez.',
  shopCredentials:
    'Shopify a refusé l’ID client ou le secret. Copiez-les à nouveau depuis les paramètres de l’application dans le Shopify Dev Dashboard.',
  shopUnavailable: 'Shopify n’a pas pu être contacté. Réessayez dans un instant.',
  mcpGenericError: 'Nous n’avons pas pu terminer cette demande. Réessayez.',
};
const ga: Copy = {
  dbSectionTitle: 'Bunachair sonraí',
  dbSectionIntro:
    'Roghnaigh cé atá in ann gach bunachar sonraí a úsáid i gComhráite agus in Aipeanna. Is féidir leat é seo a athrú anseo am ar bith.',
  dbAccessOff: 'Tá an rochtain múchta',
  dbAccessEnabled: 'Tá an rochtain cumasaithe do {count} as {total}',
  dbLoading: 'Bunachair sonraí á luchtú…',
  dbLoadError:
    'Níorbh fhéidir linn do bhunachair sonraí a luchtú. Seiceáil do nasc agus bain triail eile as.',
  queryLimitLabel: 'An t-am is faide do cheist',
  queryLimitHint:
    'Cá fhad is féidir le léamh amháin rith sula stopann sé. Is féidir le Comhráite agus Aipeanna suas leis seo a iarraidh; murach sin stopann léamh tar éis {default}.',
  queryLimitDefault: 'Réamhshocrú ({value})',
  queryLimitSource: 'Socrú naisc ({value})',
  queryLimitSaving: 'Á shábháil…',
  queryLimitSaved: 'Sábháilte',
  queryLimitError: 'Níorbh fhéidir linn an teorainn ama a shábháil. Bain triail eile as.',
  queryLimitLoadError: 'Níorbh fhéidir linn an teorainn ama a luchtú.',
  queryLimitRetry: 'Bain triail eile as',
  seconds: '{count} s',
  minutes: '{count} nóim',
  pgTlsFailed:
    'Theip ar fhíorú TLS. Seiceáil teastas an fhreastalaí nó cuir a theastas CA leis faoi TLS Casta.',
  pgAuthFailed:
    'Dhiúltaigh PostgreSQL do na dintiúir seo. Seiceáil an t-ainm úsáideora agus an pasfhocal.',
  pgNetworkFailed:
    'Níorbh fhéidir le Poneva an t-óstach seo a bhaint amach. Seiceáil ainm an óstaigh, an port agus an balla dóiteáin.',
  pgReadOnlyRequired:
    'Úsáid macasamhail inléite amháin nó cuntas bunachair shonraí tiomnaithe gan ceadanna riarthóra, cruthaithe scéimre ná athraithe róil.',
  pgNoTables:
    'Níor aimsíodh táblaí inléite ar bith. Roghnaigh bunachar sonraí ina bhfuil tábla amháin ar a laghad.',
  pgCatalogLarge:
    'Tá catalóg an bhunachair shonraí seo rómhór. Caolaigh an bunachar sonraí nó an scéimre sula mbainfidh tú triail eile as.',
  pgCatalogChanged: 'Tá catalóg an bhunachair shonraí athraithe. Déan tástáil ar an nasc arís.',
  pgUnsafeHost: 'Ní cheadaítear an t-óstach seo. Úsáid críochphointe poiblí PostgreSQL le TLS.',
  pgSetupBusy: 'Tá socrú PostgreSQL eile ar siúl cheana. Bain triail eile as ar ball beag.',
  invalidRequest: 'Seiceáil na réimsí agus bain triail eile as.',
  connectionChanged: 'Athraíodh an nasc seo in áit eile. Athlódáil é agus bain triail eile as.',
  genericStepError: 'Níorbh fhéidir linn an chéim seo a chur i gcrích. Bain triail eile as.',
  authNone: 'Gan logáil isteach',
  authBearer: 'Eochair rochtana',
  authHeaders: 'Ceanntásca saincheaptha',
  authBasic: 'Ainm úsáideora agus pasfhocal',
  authOauth: 'Logáil isteach leis an tseirbhís',
  authShopify: 'Aip Forbróra Shopify',
  transportBuiltin: 'Á rith ag Poneva',
  transportHttp: 'HTTP sruthaithe',
  transportSse: 'SSE oidhreachta',
  transportAuto: 'Braite go huathoibríoch',
  statusNeedsAuth: 'Logáil isteach de dhíth',
  statusUnavailable: 'Níl sé ar fáil',
  statusConnected: 'Nasctha',
  mcpAuthRequired:
    'Dhiúltaigh an tseirbhís do na sonraí logála isteach seo. Seiceáil iad agus bain triail eile as.',
  mcpUrlBlocked:
    'Úsáid seoladh gréasáin poiblí a thosaíonn le https://. Ní thacaítear le seoltaí líonra príobháidí.',
  mcpTimeout:
    'Thóg sé rófhada ar an tseirbhís freagairt. Seiceáil an seoladh agus bain triail eile as.',
  mcpConnectionFailed:
    'Níorbh fhéidir linn nascadh. Seiceáil an seoladh gréasáin agus na sonraí logála isteach.',
  mcpAuthorizationRequired: 'Logáil isteach arís chun an tseirbhís seo a athnascadh.',
  oauthNotConfigured:
    'Níl logáil isteach sa bhrabhsálaí cumraithe ar an suiteáil Poneva seo. Déan teagmháil le do riarthóir.',
  mcpInvalidRequest:
    'Seiceáil an seoladh gréasáin agus líon isteach sonraí don mhodh logála isteach roghnaithe.',
  shopInvalid:
    'Cuir isteach seoladh myshopify.com do shiopa, mar shampla your-store.myshopify.com.',
  shopNotFound: 'Níl aon siopa ag Shopify ag an seoladh myshopify.com sin.',
  shopNotPermitted:
    'Ní féidir an aip seo a úsáid ar an siopa sin. Suiteáil an aip ar an siopa ó Shopify Dev Dashboard, agus cinntigh go mbaineann an aip agus an siopa leis an eagraíocht Shopify chéanna.',
  shopNotInstalled:
    'Níl an aip suiteáilte ar an siopa seo fós. In Shopify Dev Dashboard, oscail an aip, roghnaigh Suiteáil aip agus roghnaigh an siopa seo, ansin bain triail eile as.',
  shopCredentials:
    'Dhiúltaigh Shopify d’aitheantas an chliaint nó don rún. Cóipeáil an dá cheann arís ó shocruithe na haipe in Shopify Dev Dashboard.',
  shopUnavailable: 'Níorbh fhéidir Shopify a bhaint amach. Bain triail eile as ar ball beag.',
  mcpGenericError: 'Níorbh fhéidir linn an t-iarratas sin a chur i gcrích. Bain triail eile as.',
};
const hr: Copy = {
  dbSectionTitle: 'Baze podataka',
  dbSectionIntro:
    'Odaberite tko može koristiti svaku bazu podataka u Chatovima i aplikacijama. To ovdje možete promijeniti u bilo kojem trenutku.',
  dbAccessOff: 'Pristup je isključen',
  dbAccessEnabled: 'Pristup je omogućen za {count} od {total}',
  dbLoading: 'Učitavanje baza podataka…',
  dbLoadError: 'Nije moguće učitati vaše baze podataka. Provjerite vezu i pokušajte ponovno.',
  queryLimitLabel: 'Najdulje trajanje upita',
  queryLimitHint:
    'Određuje koliko dugo jedno čitanje može trajati prije prekida. Chatovi i aplikacije mogu zatražiti do ovog ograničenja; u suprotnom se čitanje prekida nakon {default}.',
  queryLimitDefault: 'Zadano ({value})',
  queryLimitSource: 'Postavka veze ({value})',
  queryLimitSaving: 'Spremanje…',
  queryLimitSaved: 'Spremljeno',
  queryLimitError: 'Nije moguće spremiti vremensko ograničenje. Pokušajte ponovno.',
  queryLimitLoadError: 'Nije moguće učitati vremensko ograničenje.',
  queryLimitRetry: 'Pokušajte ponovno',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'Provjera TLS-a nije uspjela. Provjerite certifikat poslužitelja ili dodajte njegov CA certifikat u odjeljku Napredni TLS.',
  pgAuthFailed: 'PostgreSQL je odbio ove vjerodajnice. Provjerite korisničko ime i lozinku.',
  pgNetworkFailed:
    'Poneva se nije mogla povezati s ovim glavnim računalom. Provjerite naziv glavnog računala, priključak i vatrozid.',
  pgReadOnlyRequired:
    'Koristite repliku samo za čitanje ili namjenski račun baze podataka bez dopuštenja za administriranje, stvaranje shema ili promjenu uloga.',
  pgNoTables:
    'Nisu pronađene tablice koje se mogu čitati. Odaberite bazu podataka s barem jednom tablicom.',
  pgCatalogLarge:
    'Katalog ove baze podataka prevelik je. Suzite bazu podataka ili shemu prije ponovnog pokušaja.',
  pgCatalogChanged: 'Katalog baze podataka promijenio se. Ponovno testirajte vezu.',
  pgUnsafeHost: 'Ovo glavno računalo nije dopušteno. Koristite javnu TLS PostgreSQL krajnju točku.',
  pgSetupBusy: 'Drugo postavljanje PostgreSQL-a već je u tijeku. Pokušajte ponovno za trenutak.',
  invalidRequest: 'Provjerite polja i pokušajte ponovno.',
  connectionChanged:
    'Ova je veza promijenjena na drugom mjestu. Ponovno je učitajte i pokušajte ponovno.',
  genericStepError: 'Nije moguće dovršiti ovaj korak. Pokušajte ponovno.',
  authNone: 'Bez prijave',
  authBearer: 'Pristupni ključ',
  authHeaders: 'Prilagođena zaglavlja',
  authBasic: 'Korisničko ime i lozinka',
  authOauth: 'Prijava putem usluge',
  authShopify: 'Razvojna aplikacija Shopifyja',
  transportBuiltin: 'Pokreće Poneva',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Naslijeđeni SSE',
  transportAuto: 'Automatski otkriveno',
  statusNeedsAuth: 'Potrebna je prijava',
  statusUnavailable: 'Nedostupno',
  statusConnected: 'Povezano',
  mcpAuthRequired: 'Usluga je odbila ove podatke za prijavu. Provjerite ih i pokušajte ponovno.',
  mcpUrlBlocked:
    'Koristite javnu web-adresu koja počinje s https://. Adrese privatnih mreža nisu podržane.',
  mcpTimeout: 'Usluga je predugo odgovarala. Provjerite adresu i pokušajte ponovno.',
  mcpConnectionFailed: 'Nije moguće uspostaviti vezu. Provjerite web-adresu i podatke za prijavu.',
  mcpAuthorizationRequired: 'Ponovno se prijavite da biste ponovno povezali ovu uslugu.',
  oauthNotConfigured:
    'Prijava putem preglednika nije konfigurirana u ovoj instalaciji Poneve. Obratite se administratoru.',
  mcpInvalidRequest: 'Provjerite web-adresu i ispunite podatke za odabrani način prijave.',
  shopInvalid:
    'Unesite adresu svoje trgovine na myshopify.com, na primjer your-store.myshopify.com.',
  shopNotFound: 'Shopify nema trgovinu na toj adresi myshopify.com.',
  shopNotPermitted:
    'Ova se aplikacija ne može koristiti u toj trgovini. Instalirajte aplikaciju u trgovinu putem Shopifyjevog Dev Dashboarda i provjerite pripadaju li aplikacija i trgovina istoj Shopify organizaciji.',
  shopNotInstalled:
    'Aplikacija još nije instalirana u ovoj trgovini. U Shopifyjevom Dev Dashboardu otvorite aplikaciju, odaberite Install app i izaberite ovu trgovinu, a zatim pokušajte ponovno.',
  shopCredentials:
    'Shopify je odbio ID klijenta ili tajnu. Ponovno kopirajte oboje iz postavki aplikacije u Shopifyjevu Dev Dashboardu.',
  shopUnavailable: 'Nije moguće povezati se sa Shopifyjem. Pokušajte ponovno za trenutak.',
  mcpGenericError: 'Nije moguće dovršiti taj zahtjev. Pokušajte ponovno.',
};
const hu: Copy = {
  dbSectionTitle: 'Adatbázisok',
  dbSectionIntro:
    'Válaszd ki, hogy ki használhatja az egyes adatbázisokat a csevegésekben és az alkalmazásokban. Ezt bármikor módosíthatod itt.',
  dbAccessOff: 'A hozzáférés ki van kapcsolva',
  dbAccessEnabled: 'A hozzáférés {count} / {total} adatbázishoz engedélyezve',
  dbLoading: 'Adatbázisok betöltése…',
  dbLoadError:
    'Nem sikerült betölteni az adatbázisokat. Ellenőrizd a kapcsolatot, majd próbáld újra.',
  queryLimitLabel: 'Leghosszabb lekérdezési idő',
  queryLimitHint:
    'Megadja, hogy egy olvasási művelet mennyi ideig futhat, mielőtt leáll. A csevegések és az alkalmazások legfeljebb ezt az időt kérhetik; ellenkező esetben az olvasási művelet {default} után leáll.',
  queryLimitDefault: 'Alapértelmezett ({value})',
  queryLimitSource: 'Kapcsolat beállítása ({value})',
  queryLimitSaving: 'Mentés…',
  queryLimitSaved: 'Mentve',
  queryLimitError: 'Nem sikerült menteni az időkorlátot. Próbáld újra.',
  queryLimitLoadError: 'Nem sikerült betölteni az időkorlátot.',
  queryLimitRetry: 'Próbáld újra',
  seconds: '{count} mp',
  minutes: '{count} perc',
  pgTlsFailed:
    'A TLS-ellenőrzés sikertelen. Ellenőrizd a szervertanúsítványt, vagy add hozzá a hitelesítésszolgáltató tanúsítványát a Speciális TLS-beállításoknál.',
  pgAuthFailed:
    'A PostgreSQL elutasította ezeket a hitelesítési adatokat. Ellenőrizd a felhasználónevet és a jelszót.',
  pgNetworkFailed:
    'A Poneva nem tudta elérni ezt a gazdagépet. Ellenőrizd a gazdagép nevét, a portot és a tűzfalat.',
  pgReadOnlyRequired:
    'Használj csak olvasható replikát vagy dedikált adatbázis-fiókot rendszergazdai, séma-létrehozási és szerepkörváltási jogosultságok nélkül.',
  pgNoTables: 'Nem találhatók olvasható táblák. Válassz legalább egy táblát tartalmazó adatbázist.',
  pgCatalogLarge:
    'Az adatbázis-katalógus túl nagy. Próbálkozás előtt szűkítsd az adatbázist vagy a sémát.',
  pgCatalogChanged: 'Az adatbázis-katalógus megváltozott. Teszteld újra a kapcsolatot.',
  pgUnsafeHost: 'Ez a gazdagép nem engedélyezett. Használj nyilvános TLS-es PostgreSQL-végpontot.',
  pgSetupBusy: 'Egy másik PostgreSQL-beállítás már folyamatban van. Próbáld újra kis idő múlva.',
  invalidRequest: 'Ellenőrizd a mezőket, majd próbáld újra.',
  connectionChanged: 'Ezt a kapcsolatot máshol módosították. Töltsd újra, majd próbáld újra.',
  genericStepError: 'Nem sikerült végrehajtani ezt a lépést. Próbáld újra.',
  authNone: 'Nincs bejelentkezés',
  authBearer: 'Hozzáférési kulcs',
  authHeaders: 'Egyéni fejlécek',
  authBasic: 'Felhasználónév és jelszó',
  authOauth: 'Bejelentkezés a szolgáltatással',
  authShopify: 'Shopify fejlesztői alkalmazás',
  transportBuiltin: 'A Poneva futtatja',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Korábbi SSE',
  transportAuto: 'Automatikusan felismerve',
  statusNeedsAuth: 'Bejelentkezés szükséges',
  statusUnavailable: 'Nem érhető el',
  statusConnected: 'Csatlakoztatva',
  mcpAuthRequired:
    'A szolgáltatás elutasította ezeket a bejelentkezési adatokat. Ellenőrizd őket, majd próbáld újra.',
  mcpUrlBlocked:
    'Adj meg https:// kezdetű, nyilvános webcímet. A privát hálózati címek nem támogatottak.',
  mcpTimeout: 'A szolgáltatás túl sokáig nem válaszolt. Ellenőrizd a címet, majd próbáld újra.',
  mcpConnectionFailed:
    'Nem sikerült csatlakozni. Ellenőrizd a webcímet és a bejelentkezési adatokat.',
  mcpAuthorizationRequired: 'Jelentkezz be újra a szolgáltatás újracsatlakoztatásához.',
  oauthNotConfigured:
    'A böngészős bejelentkezés nincs beállítva ezen a Poneva-telepítésen. Vedd fel a kapcsolatot a rendszergazdával.',
  mcpInvalidRequest:
    'Ellenőrizd a webcímet, és add meg a kiválasztott bejelentkezési módhoz szükséges adatokat.',
  shopInvalid: 'Add meg az áruházad myshopify.com-címét, például: your-store.myshopify.com.',
  shopNotFound: 'A Shopify-nál nincs áruház ezen a myshopify.com-címen.',
  shopNotPermitted:
    'Ez az alkalmazás nem használható ebben az áruházban. Telepítsd az alkalmazást az áruházba a Shopify Dev Dashboardról, és győződj meg róla, hogy az alkalmazás és az áruház ugyanahhoz a Shopify-szervezethez tartozik.',
  shopNotInstalled:
    'Az alkalmazás még nincs telepítve ebbe az áruházba. A Shopify Dev Dashboardon nyisd meg az alkalmazást, válaszd az Install app lehetőséget, jelöld ki ezt az áruházat, majd próbáld újra.',
  shopCredentials:
    'A Shopify elutasította az ügyfélazonosítót vagy a titkos kulcsot. Másold ki újra mindkettőt az alkalmazás beállításaiból a Shopify Dev Dashboardon.',
  shopUnavailable: 'A Shopify nem érhető el. Próbáld újra kis idő múlva.',
  mcpGenericError: 'Nem sikerült végrehajtani a kérést. Próbáld újra.',
};
const it: Copy = {
  dbSectionTitle: 'Database',
  dbSectionIntro:
    'Scegli chi può usare ogni database nelle Chat e nelle App. Puoi modificarlo qui in qualsiasi momento.',
  dbAccessOff: 'Accesso disattivato',
  dbAccessEnabled: 'Accesso abilitato per {count} di {total}',
  dbLoading: 'Caricamento dei database…',
  dbLoadError:
    'Non è stato possibile caricare i tuoi database. Controlla la connessione e riprova.',
  queryLimitLabel: 'Durata massima della query',
  queryLimitHint:
    'Indica per quanto tempo può essere eseguita una singola lettura prima di interrompersi. Chat e App possono richiedere fino a questo limite; altrimenti la lettura si interrompe dopo {default}.',
  queryLimitDefault: 'Predefinito ({value})',
  queryLimitSource: 'Impostazione della connessione ({value})',
  queryLimitSaving: 'Salvataggio…',
  queryLimitSaved: 'Salvato',
  queryLimitError: 'Non è stato possibile salvare il limite di tempo. Riprova.',
  queryLimitLoadError: 'Non è stato possibile caricare il limite di tempo.',
  queryLimitRetry: 'Riprova',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'Verifica TLS non riuscita. Controlla il certificato del server o aggiungi il relativo certificato CA in TLS avanzato.',
  pgAuthFailed:
    'PostgreSQL ha rifiutato queste credenziali. Controlla il nome utente e la password.',
  pgNetworkFailed:
    'Poneva non ha potuto raggiungere questo host. Controlla il nome host, la porta e il firewall.',
  pgReadOnlyRequired:
    'Usa una replica di sola lettura o un account database dedicato senza autorizzazioni di amministratore, creazione di schemi o cambio di ruolo.',
  pgNoTables:
    'Non sono state trovate tabelle leggibili. Scegli un database con almeno una tabella.',
  pgCatalogLarge:
    'Il catalogo di questo database è troppo grande. Restringi il database o lo schema prima di riprovare.',
  pgCatalogChanged: 'Il catalogo del database è cambiato. Verifica di nuovo la connessione.',
  pgUnsafeHost: 'Questo host non è consentito. Usa un endpoint PostgreSQL pubblico con TLS.',
  pgSetupBusy: 'È già in corso un’altra configurazione PostgreSQL. Riprova tra poco.',
  invalidRequest: 'Controlla i campi e riprova.',
  connectionChanged: 'Questa connessione è stata modificata altrove. Ricaricala e riprova.',
  genericStepError: 'Non è stato possibile completare questo passaggio. Riprova.',
  authNone: 'Nessuna autenticazione',
  authBearer: 'Chiave di accesso',
  authHeaders: 'Intestazioni personalizzate',
  authBasic: 'Nome utente e password',
  authOauth: 'Accedi con il servizio',
  authShopify: 'App Shopify per sviluppatori',
  transportBuiltin: 'Eseguito da Poneva',
  transportHttp: 'HTTP trasmissibile',
  transportSse: 'SSE legacy',
  transportAuto: 'Rilevato automaticamente',
  statusNeedsAuth: 'Richiede accesso',
  statusUnavailable: 'Non disponibile',
  statusConnected: 'Connesso',
  mcpAuthRequired: 'Il servizio ha rifiutato questi dati di accesso. Controllali e riprova.',
  mcpUrlBlocked:
    'Usa un indirizzo web pubblico che inizi con https://. Gli indirizzi di rete privata non sono supportati.',
  mcpTimeout:
    'Il servizio ha impiegato troppo tempo per rispondere. Controlla l’indirizzo e riprova.',
  mcpConnectionFailed:
    'Non è stato possibile connettersi. Controlla l’indirizzo web e i dati di accesso.',
  mcpAuthorizationRequired: 'Accedi di nuovo per riconnettere questo servizio.',
  oauthNotConfigured:
    'L’accesso tramite browser non è configurato in questa installazione di Poneva. Contatta l’amministratore.',
  mcpInvalidRequest:
    'Controlla l’indirizzo web e compila i dati per il metodo di accesso selezionato.',
  shopInvalid:
    'Inserisci l’indirizzo myshopify.com del tuo negozio, ad esempio tuo-negozio.myshopify.com.',
  shopNotFound: 'Shopify non ha alcun negozio associato a questo indirizzo myshopify.com.',
  shopNotPermitted:
    'Questa app non può essere usata su quel negozio. Installa l’app nel negozio dalla Dev Dashboard di Shopify e assicurati che l’app e il negozio appartengano alla stessa organizzazione Shopify.',
  shopNotInstalled:
    'L’app non è ancora installata in questo negozio. Nella Dev Dashboard di Shopify, apri l’app, scegli Installa app e seleziona questo negozio, quindi riprova.',
  shopCredentials:
    'Shopify ha rifiutato l’ID client o il segreto. Copiali di nuovo dalle impostazioni dell’app nella Dev Dashboard di Shopify.',
  shopUnavailable: 'Non è stato possibile raggiungere Shopify. Riprova tra poco.',
  mcpGenericError: 'Non è stato possibile completare la richiesta. Riprova.',
};
const lt: Copy = {
  dbSectionTitle: 'Duomenų bazės',
  dbSectionIntro:
    'Pasirinkite, kas gali naudoti kiekvieną duomenų bazę pokalbiuose ir programėlėse. Tai galite bet kada pakeisti čia.',
  dbAccessOff: 'Prieiga išjungta',
  dbAccessEnabled: 'Prieiga įjungta {count} iš {total}',
  dbLoading: 'Įkeliamos duomenų bazės…',
  dbLoadError: 'Nepavyko įkelti jūsų duomenų bazių. Patikrinkite ryšį ir bandykite dar kartą.',
  queryLimitLabel: 'Ilgiausia užklausos trukmė',
  queryLimitHint:
    'Kiek laiko gali būti vykdoma viena skaitymo operacija, kol ji sustabdoma. Pokalbiai ir programėlės gali prašyti iki šios trukmės; kitu atveju skaitymo operacija sustabdoma po {default}.',
  queryLimitDefault: 'Numatytoji ({value})',
  queryLimitSource: 'Ryšio nustatymas ({value})',
  queryLimitSaving: 'Išsaugoma…',
  queryLimitSaved: 'Išsaugota',
  queryLimitError: 'Nepavyko išsaugoti laiko limito. Bandykite dar kartą.',
  queryLimitLoadError: 'Nepavyko įkelti laiko limito.',
  queryLimitRetry: 'Bandyti dar kartą',
  seconds: '{count} sek.',
  minutes: '{count} min.',
  pgTlsFailed:
    'Nepavyko patikrinti TLS. Patikrinkite serverio sertifikatą arba skiltyje „Išplėstinis TLS“ pridėkite jo CA sertifikatą.',
  pgAuthFailed:
    'PostgreSQL atmetė šiuos prisijungimo duomenis. Patikrinkite naudotojo vardą ir slaptažodį.',
  pgNetworkFailed:
    'Poneva nepavyko pasiekti šio pagrindinio kompiuterio. Patikrinkite pagrindinio kompiuterio vardą, prievadą ir užkardą.',
  pgReadOnlyRequired:
    'Naudokite tik skaitymui skirtą repliką arba specialią duomenų bazės paskyrą, neturinčią administratoriaus, schemų kūrimo ar vaidmenų keitimo teisių.',
  pgNoTables:
    'Nerasta jokių skaitomų lentelių. Pasirinkite duomenų bazę, kurioje yra bent viena lentelė.',
  pgCatalogLarge:
    'Šios duomenų bazės katalogas per didelis. Prieš bandydami dar kartą susiaurinkite duomenų bazę arba schemą.',
  pgCatalogChanged: 'Duomenų bazės katalogas pasikeitė. Dar kartą patikrinkite ryšį.',
  pgUnsafeHost:
    'Šis pagrindinis kompiuteris neleidžiamas. Naudokite viešai pasiekiamą TLS PostgreSQL galinį tašką.',
  pgSetupBusy: 'Kitas PostgreSQL nustatymas jau vykdomas. Po akimirkos bandykite dar kartą.',
  invalidRequest: 'Patikrinkite laukus ir bandykite dar kartą.',
  connectionChanged: 'Šis ryšys buvo pakeistas kitur. Įkelkite jį iš naujo ir bandykite dar kartą.',
  genericStepError: 'Nepavyko užbaigti šio veiksmo. Bandykite dar kartą.',
  authNone: 'Be prisijungimo',
  authBearer: 'Prieigos raktas',
  authHeaders: 'Pasirinktinės antraštės',
  authBasic: 'Naudotojo vardas ir slaptažodis',
  authOauth: 'Prisijungti naudojant paslaugą',
  authShopify: 'Shopify kūrėjo programėlė',
  transportBuiltin: 'Vykdo Poneva',
  transportHttp: 'Srautinį perdavimą palaikantis HTTP',
  transportSse: 'Senasis SSE',
  transportAuto: 'Aptikta automatiškai',
  statusNeedsAuth: 'Reikia prisijungti',
  statusUnavailable: 'Nepasiekiama',
  statusConnected: 'Prisijungta',
  mcpAuthRequired:
    'Paslauga atmetė šiuos prisijungimo duomenis. Patikrinkite juos ir bandykite dar kartą.',
  mcpUrlBlocked:
    'Naudokite viešą žiniatinklio adresą, prasidedantį https://. Privatūs tinklo adresai nepalaikomi.',
  mcpTimeout: 'Paslauga per ilgai neatsakė. Patikrinkite adresą ir bandykite dar kartą.',
  mcpConnectionFailed:
    'Nepavyko prisijungti. Patikrinkite žiniatinklio adresą ir prisijungimo duomenis.',
  mcpAuthorizationRequired: 'Prisijunkite dar kartą, kad iš naujo prijungtumėte šią paslaugą.',
  oauthNotConfigured:
    'Naršyklės prisijungimas šioje Poneva diegimo versijoje nesukonfigūruotas. Susisiekite su administratoriumi.',
  mcpInvalidRequest:
    'Patikrinkite žiniatinklio adresą ir įveskite pasirinktam prisijungimo būdui reikalingus duomenis.',
  shopInvalid:
    'Įveskite savo parduotuvės myshopify.com adresą, pavyzdžiui, your-store.myshopify.com.',
  shopNotFound: 'Shopify neturi parduotuvės tokiu myshopify.com adresu.',
  shopNotPermitted:
    'Šios programėlės negalima naudoti toje parduotuvėje. Įdiekite programėlę parduotuvėje naudodami Shopify kūrėjų skydą ir įsitikinkite, kad programėlė ir parduotuvė priklauso tai pačiai Shopify organizacijai.',
  shopNotInstalled:
    'Programėlė šioje parduotuvėje dar neįdiegta. Shopify kūrėjų skyde atidarykite programėlę, pasirinkite „Įdiegti programėlę“, pasirinkite šią parduotuvę ir bandykite dar kartą.',
  shopCredentials:
    'Shopify atmetė kliento ID arba slaptąjį raktą. Nukopijuokite abu dar kartą iš programėlės nustatymų Shopify kūrėjų skyde.',
  shopUnavailable: 'Nepavyko pasiekti Shopify. Po akimirkos bandykite dar kartą.',
  mcpGenericError: 'Nepavyko įvykdyti šios užklausos. Bandykite dar kartą.',
};
const lv: Copy = {
  dbSectionTitle: 'Datu bāzes',
  dbSectionIntro:
    'Izvēlieties, kas var izmantot katru datu bāzi tērzētavās un lietotnēs. Šo iestatījumu varat mainīt jebkurā laikā.',
  dbAccessOff: 'Piekļuve izslēgta',
  dbAccessEnabled: 'Piekļuve iespējota {count} no {total}',
  dbLoading: 'Notiek datu bāzu ielāde…',
  dbLoadError: 'Neizdevās ielādēt jūsu datu bāzes. Pārbaudiet savienojumu un mēģiniet vēlreiz.',
  queryLimitLabel: 'Ilgākais vaicājuma izpildes laiks',
  queryLimitHint:
    'Cik ilgi drīkst darboties viens lasīšanas vaicājums, pirms tas tiek apturēts. Tērzētavas un lietotnes var pieprasīt līdz šim laikam; pretējā gadījumā lasīšana tiek apturēta pēc {default}.',
  queryLimitDefault: 'Noklusējums ({value})',
  queryLimitSource: 'Savienojuma iestatījums ({value})',
  queryLimitSaving: 'Notiek saglabāšana…',
  queryLimitSaved: 'Saglabāts',
  queryLimitError: 'Neizdevās saglabāt laika ierobežojumu. Mēģiniet vēlreiz.',
  queryLimitLoadError: 'Neizdevās ielādēt laika ierobežojumu.',
  queryLimitRetry: 'Mēģināt vēlreiz',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'TLS pārbaude neizdevās. Pārbaudiet servera sertifikātu vai sadaļā “Papildu TLS” pievienojiet tā CA sertifikātu.',
  pgAuthFailed: 'PostgreSQL noraidīja šos piekļuves datus. Pārbaudiet lietotājvārdu un paroli.',
  pgNetworkFailed:
    'Poneva nevarēja sasniegt šo resursdatoru. Pārbaudiet resursdatora nosaukumu, portu un ugunsmūri.',
  pgReadOnlyRequired:
    'Izmantojiet tikai lasāmu repliku vai īpašu datu bāzes kontu bez administratora, shēmu izveides vai lomu pārslēgšanas atļaujām.',
  pgNoTables:
    'Netika atrasta neviena lasāma tabula. Izvēlieties datu bāzi, kurā ir vismaz viena tabula.',
  pgCatalogLarge:
    'Šīs datu bāzes katalogs ir pārāk liels. Pirms atkārtota mēģinājuma sašauriniet datu bāzes vai shēmas atlasi.',
  pgCatalogChanged: 'Datu bāzes katalogs ir mainījies. Vēlreiz pārbaudiet savienojumu.',
  pgUnsafeHost:
    'Šis resursdators nav atļauts. Izmantojiet publiski pieejamu TLS PostgreSQL galapunktu.',
  pgSetupBusy: 'Jau notiek cita PostgreSQL iestatīšana. Pēc brīža mēģiniet vēlreiz.',
  invalidRequest: 'Pārbaudiet laukus un mēģiniet vēlreiz.',
  connectionChanged:
    'Šis savienojums citur ir mainīts. Ielādējiet to atkārtoti un mēģiniet vēlreiz.',
  genericStepError: 'Neizdevās pabeigt šo darbību. Mēģiniet vēlreiz.',
  authNone: 'Bez pierakstīšanās',
  authBearer: 'Piekļuves atslēga',
  authHeaders: 'Pielāgotas galvenes',
  authBasic: 'Lietotājvārds un parole',
  authOauth: 'Pierakstīties ar pakalpojumu',
  authShopify: 'Shopify izstrādātāja lietotne',
  transportBuiltin: 'Palaiž Poneva',
  transportHttp: 'Straumējams HTTP',
  transportSse: 'Mantots SSE',
  transportAuto: 'Noteikts automātiski',
  statusNeedsAuth: 'Nepieciešama pierakstīšanās',
  statusUnavailable: 'Nav pieejams',
  statusConnected: 'Savienots',
  mcpAuthRequired:
    'Pakalpojums noraidīja šos pierakstīšanās datus. Pārbaudiet tos un mēģiniet vēlreiz.',
  mcpUrlBlocked:
    'Izmantojiet publisku tīmekļa adresi, kas sākas ar https://. Privātā tīkla adreses netiek atbalstītas.',
  mcpTimeout: 'Pakalpojums atbildēja pārāk ilgi. Pārbaudiet adresi un mēģiniet vēlreiz.',
  mcpConnectionFailed:
    'Neizdevās izveidot savienojumu. Pārbaudiet tīmekļa adresi un pierakstīšanās datus.',
  mcpAuthorizationRequired:
    'Lai atkārtoti izveidotu savienojumu ar šo pakalpojumu, pierakstieties vēlreiz.',
  oauthNotConfigured:
    'Pārlūkprogrammas pierakstīšanās šajā Poneva instalācijā nav konfigurēta. Sazinieties ar administratoru.',
  mcpInvalidRequest:
    'Pārbaudiet tīmekļa adresi un aizpildiet atlasītajai pierakstīšanās metodei nepieciešamo informāciju.',
  shopInvalid: 'Ievadiet sava veikala myshopify.com adresi, piemēram, your-store.myshopify.com.',
  shopNotFound: 'Shopify nav veikala ar šo myshopify.com adresi.',
  shopNotPermitted:
    'Šo lietotni šajā veikalā nevar izmantot. Instalējiet lietotni veikalā Shopify izstrādātāju informācijas panelī un pārliecinieties, ka lietotne un veikals pieder tai pašai Shopify organizācijai.',
  shopNotInstalled:
    'Lietotne šajā veikalā vēl nav instalēta. Shopify izstrādātāju informācijas panelī atveriet lietotni, izvēlieties “Install app” un atlasiet šo veikalu, pēc tam mēģiniet vēlreiz.',
  shopCredentials:
    'Shopify noraidīja klienta ID vai slepeno atslēgu. Nokopējiet abus vēlreiz lietotnes iestatījumos Shopify izstrādātāju informācijas panelī.',
  shopUnavailable: 'Shopify nav sasniedzams. Pēc brīža mēģiniet vēlreiz.',
  mcpGenericError: 'Neizdevās pabeigt šo pieprasījumu. Mēģiniet vēlreiz.',
};
const mt: Copy = {
  dbSectionTitle: 'Databases',
  dbSectionIntro:
    'Agħżel min jista’ juża kull database fiċ-Chats u fl-Apps. Tista’ tibdel dan hawn fi kwalunkwe ħin.',
  dbAccessOff: 'L-aċċess huwa mitfi',
  dbAccessEnabled: 'L-aċċess huwa attivat għal {count} minn {total}',
  dbLoading: 'Qed jitgħabbew id-databases…',
  dbLoadError:
    'Ma stajniex nitgħabbew id-databases tiegħek. Iċċekkja l-konnessjoni tiegħek u erġa’ pprova.',
  queryLimitLabel: 'L-itwal ħin ta’ mistoqsija',
  queryLimitHint:
    'Kemm tista’ ddum mistoqsija waħda ta’ qari qabel ma tieqaf. Iċ-Chats u l-Apps jistgħu jitolbu sa dan il-limitu; inkella qari jieqaf wara {default}.',
  queryLimitDefault: 'Default ({value})',
  queryLimitSource: 'Issettjar tal-konnessjoni ({value})',
  queryLimitSaving: 'Qed jiġi salvat…',
  queryLimitSaved: 'Issejvjat',
  queryLimitError: 'Ma stajniex insalvaw il-limitu tal-ħin. Erġa’ pprova.',
  queryLimitLoadError: 'Ma stajniex nitgħabbew il-limitu tal-ħin.',
  queryLimitRetry: 'Erġa’ pprova',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'Il-verifika TLS falliet. Iċċekkja ċ-ċertifikat tas-server jew żid iċ-ċertifikat CA tiegħu taħt TLS Avvanzat.',
  pgAuthFailed: 'PostgreSQL irrifjuta dawn il-kredenzjali. Iċċekkja l-isem tal-utent u l-password.',
  pgNetworkFailed:
    'Poneva ma setgħetx tilħaq dan il-host. Iċċekkja l-isem tal-host, il-port u l-firewall.',
  pgReadOnlyRequired:
    'Uża replika li tinqara biss jew kont tad-database dedikat mingħajr permessi ta’ amministratur, ħolqien ta’ skemi jew bdil ta’ rwoli.',
  pgNoTables: 'Ma nstabux tabelli li jinqraw. Agħżel database b’tal-inqas tabella waħda.',
  pgCatalogLarge:
    'Il-katalgu ta’ din id-database huwa kbir wisq. Naqqas id-database jew l-iskema qabel terġa’ tipprova.',
  pgCatalogChanged: 'Il-katalgu tad-database inbidel. Erġa’ ttestja l-konnessjoni.',
  pgUnsafeHost: 'Dan il-host mhux permess. Uża endpoint PostgreSQL pubbliku b’TLS.',
  pgSetupBusy: 'Setup ieħor ta’ PostgreSQL diġà għaddej. Erġa’ pprova wara ftit.',
  invalidRequest: 'Iċċekkja l-oqsma u erġa’ pprova.',
  connectionChanged: 'Din il-konnessjoni nbidlet x’imkien ieħor. Erġa’ għabbiha u erġa’ pprova.',
  genericStepError: 'Ma stajniex inlestu dan il-pass. Erġa’ pprova.',
  authNone: 'Ebda login',
  authBearer: 'Ċavetta tal-aċċess',
  authHeaders: 'Headers personalizzati',
  authBasic: 'Isem tal-utent u password',
  authOauth: 'Idħol bis-servizz',
  authShopify: 'Shopify Developer App',
  transportBuiltin: 'Imħaddem minn Poneva',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Legacy SSE',
  transportAuto: 'Individwat awtomatikament',
  statusNeedsAuth: 'Jeħtieġ login',
  statusUnavailable: 'Mhux disponibbli',
  statusConnected: 'Konness',
  mcpAuthRequired: 'Is-servizz irrifjuta dawn id-dettalji tal-login. Iċċekkjahom u erġa’ pprova.',
  mcpUrlBlocked:
    'Uża indirizz pubbliku tal-web li jibda b’https://. Indirizzi ta’ netwerks privati mhumiex appoġġjati.',
  mcpTimeout: 'Is-servizz dam wisq biex iwieġeb. Iċċekkja l-indirizz u erġa’ pprova.',
  mcpConnectionFailed:
    'Ma stajniex nikkonnettjaw. Iċċekkja l-indirizz tal-web u d-dettalji tal-login.',
  mcpAuthorizationRequired: 'Erġa’ idħol biex terġa’ tikkonnettja dan is-servizz.',
  oauthNotConfigured:
    'Il-login permezz tal-browser mhuwiex ikkonfigurat fuq din l-installazzjoni ta’ Poneva. Ikkuntattja lill-amministratur tiegħek.',
  mcpInvalidRequest:
    'Iċċekkja l-indirizz tal-web u imla d-dettalji għall-metodu tal-login magħżul.',
  shopInvalid:
    'Daħħal l-indirizz myshopify.com tal-ħanut tiegħek, pereżempju your-store.myshopify.com.',
  shopNotFound: 'Shopify m’għandhiex ħanut f’dan l-indirizz myshopify.com.',
  shopNotPermitted:
    'Din l-app ma tistax tintuża f’dan il-ħanut. Installa l-app fil-ħanut mid-Dev Dashboard ta’ Shopify, u kun żgur li l-app u l-ħanut jappartjenu lill-istess organizzazzjoni Shopify.',
  shopNotInstalled:
    'L-app għadha mhix installata f’dan il-ħanut. Fid-Dev Dashboard ta’ Shopify, iftaħ l-app, agħżel Install app u agħżel dan il-ħanut, imbagħad erġa’ pprova.',
  shopCredentials:
    'Shopify irrifjutat il-client ID jew is-secret. Ikkopjahom it-tnejn mill-ġdid mis-settings tal-app fid-Dev Dashboard ta’ Shopify.',
  shopUnavailable: 'Ma stajniex nilħqu lil Shopify. Erġa’ pprova wara ftit.',
  mcpGenericError: 'Ma stajniex inlestu dik it-talba. Erġa’ pprova.',
};
const nl: Copy = {
  dbSectionTitle: 'Databases',
  dbSectionIntro:
    'Kies wie elke database in Chats en Apps kan gebruiken. Je kunt dit hier altijd wijzigen.',
  dbAccessOff: 'Toegang uitgeschakeld',
  dbAccessEnabled: 'Toegang ingeschakeld voor {count} van {total}',
  dbLoading: 'Databases worden geladen…',
  dbLoadError:
    'We konden je databases niet laden. Controleer je verbinding en probeer het opnieuw.',
  queryLimitLabel: 'Langste queryduur',
  queryLimitHint:
    'Hoelang één leesbewerking mag duren voordat deze wordt gestopt. Chats en Apps kunnen dit maximaal aanvragen; anders stopt een leesbewerking na {default}.',
  queryLimitDefault: 'Standaard ({value})',
  queryLimitSource: 'Verbindingsinstelling ({value})',
  queryLimitSaving: 'Opslaan…',
  queryLimitSaved: 'Opgeslagen',
  queryLimitError: 'We konden de tijdslimiet niet opslaan. Probeer het opnieuw.',
  queryLimitLoadError: 'We konden de tijdslimiet niet laden.',
  queryLimitRetry: 'Opnieuw proberen',
  seconds: '{count} sec.',
  minutes: '{count} min.',
  pgTlsFailed:
    'TLS-verificatie mislukt. Controleer het servercertificaat of voeg het CA-certificaat toe onder Geavanceerde TLS.',
  pgAuthFailed:
    'PostgreSQL heeft deze inloggegevens geweigerd. Controleer de gebruikersnaam en het wachtwoord.',
  pgNetworkFailed: 'Poneva kon deze host niet bereiken. Controleer de hostnaam, poort en firewall.',
  pgReadOnlyRequired:
    'Gebruik een alleen-lezenreplica of een speciaal databaseaccount zonder rechten voor beheer, schemacreatie of het wisselen van rollen.',
  pgNoTables:
    'Er zijn geen leesbare tabellen gevonden. Kies een database met ten minste één tabel.',
  pgCatalogLarge:
    'De catalogus van deze database is te groot. Verklein de database of het schema voordat je het opnieuw probeert.',
  pgCatalogChanged: 'De catalogus van de database is gewijzigd. Test de verbinding opnieuw.',
  pgUnsafeHost: 'Deze host is niet toegestaan. Gebruik een openbaar TLS-eindpunt voor PostgreSQL.',
  pgSetupBusy:
    'Er wordt al een andere PostgreSQL-installatie uitgevoerd. Probeer het over enkele ogenblikken opnieuw.',
  invalidRequest: 'Controleer de velden en probeer het opnieuw.',
  connectionChanged:
    'Deze verbinding is ergens anders gewijzigd. Laad de verbinding opnieuw en probeer het opnieuw.',
  genericStepError: 'We konden deze stap niet voltooien. Probeer het opnieuw.',
  authNone: 'Geen aanmelding',
  authBearer: 'Toegangssleutel',
  authHeaders: 'Aangepaste headers',
  authBasic: 'Gebruikersnaam en wachtwoord',
  authOauth: 'Aanmelden bij de service',
  authShopify: 'Shopify Developer App',
  transportBuiltin: 'Uitgevoerd door Poneva',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Verouderde SSE',
  transportAuto: 'Automatisch gedetecteerd',
  statusNeedsAuth: 'Aanmelding vereist',
  statusUnavailable: 'Niet beschikbaar',
  statusConnected: 'Verbonden',
  mcpAuthRequired:
    'De service heeft deze aanmeldgegevens geweigerd. Controleer ze en probeer het opnieuw.',
  mcpUrlBlocked:
    'Gebruik een openbaar webadres dat begint met https://. Adressen van privénetwerken worden niet ondersteund.',
  mcpTimeout: 'De service reageerde te laat. Controleer het adres en probeer het opnieuw.',
  mcpConnectionFailed:
    'We konden geen verbinding maken. Controleer het webadres en de aanmeldgegevens.',
  mcpAuthorizationRequired: 'Meld je opnieuw aan om deze service opnieuw te verbinden.',
  oauthNotConfigured:
    'Aanmelden via de browser is niet geconfigureerd voor deze Poneva-installatie. Neem contact op met je beheerder.',
  mcpInvalidRequest:
    'Controleer het webadres en vul de gegevens in voor de geselecteerde aanmeldmethode.',
  shopInvalid:
    'Voer het myshopify.com-adres van je winkel in, bijvoorbeeld your-store.myshopify.com.',
  shopNotFound: 'Shopify heeft geen winkel op dat myshopify.com-adres.',
  shopNotPermitted:
    'Deze app kan niet in die winkel worden gebruikt. Installeer de app in de winkel via het Shopify Dev Dashboard en controleer of de app en de winkel bij dezelfde Shopify-organisatie horen.',
  shopNotInstalled:
    'De app is nog niet in deze winkel geïnstalleerd. Open de app in het Shopify Dev Dashboard, kies App installeren en selecteer deze winkel. Probeer het daarna opnieuw.',
  shopCredentials:
    'Shopify heeft de client-ID of het geheim geweigerd. Kopieer beide opnieuw uit de instellingen van de app in het Shopify Dev Dashboard.',
  shopUnavailable: 'Shopify kon niet worden bereikt. Probeer het over enkele ogenblikken opnieuw.',
  mcpGenericError: 'We konden dat verzoek niet voltooien. Probeer het opnieuw.',
};
const pl: Copy = {
  dbSectionTitle: 'Bazy danych',
  dbSectionIntro:
    'Wybierz, kto może korzystać z poszczególnych baz danych w Czatach i Aplikacjach. Możesz to tutaj zmienić w dowolnym momencie.',
  dbAccessOff: 'Dostęp wyłączony',
  dbAccessEnabled: 'Dostęp włączony dla {count} z {total}',
  dbLoading: 'Ładowanie baz danych…',
  dbLoadError: 'Nie udało się załadować baz danych. Sprawdź połączenie i spróbuj ponownie.',
  queryLimitLabel: 'Maksymalny czas zapytania',
  queryLimitHint:
    'Jak długo może działać pojedynczy odczyt, zanim zostanie zatrzymany. Czaty i Aplikacje mogą żądać czasu do tej wartości; w przeciwnym razie odczyt zostanie zatrzymany po {default}.',
  queryLimitDefault: 'Domyślnie ({value})',
  queryLimitSource: 'Ustawienie połączenia ({value})',
  queryLimitSaving: 'Zapisywanie…',
  queryLimitSaved: 'Zapisano',
  queryLimitError: 'Nie udało się zapisać limitu czasu. Spróbuj ponownie.',
  queryLimitLoadError: 'Nie udało się załadować limitu czasu.',
  queryLimitRetry: 'Spróbuj ponownie',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'Weryfikacja TLS nie powiodła się. Sprawdź certyfikat serwera lub dodaj jego certyfikat CA w sekcji Zaawansowane ustawienia TLS.',
  pgAuthFailed: 'PostgreSQL odrzucił te dane logowania. Sprawdź nazwę użytkownika i hasło.',
  pgNetworkFailed:
    'Poneva nie mogła połączyć się z tym hostem. Sprawdź nazwę hosta, port i zaporę sieciową.',
  pgReadOnlyRequired:
    'Użyj repliki tylko do odczytu lub dedykowanego konta bazy danych bez uprawnień administratora, tworzenia schematów ani zmiany ról.',
  pgNoTables:
    'Nie znaleziono żadnych tabel możliwych do odczytu. Wybierz bazę danych zawierającą co najmniej jedną tabelę.',
  pgCatalogLarge:
    'Katalog tej bazy danych jest zbyt duży. Przed ponowną próbą zawęź bazę danych lub schemat.',
  pgCatalogChanged: 'Katalog bazy danych uległ zmianie. Ponownie przetestuj połączenie.',
  pgUnsafeHost: 'Ten host nie jest dozwolony. Użyj publicznego punktu końcowego PostgreSQL z TLS.',
  pgSetupBusy: 'Inna konfiguracja PostgreSQL jest już w toku. Spróbuj ponownie za chwilę.',
  invalidRequest: 'Sprawdź pola i spróbuj ponownie.',
  connectionChanged:
    'To połączenie zostało zmienione w innym miejscu. Załaduj je ponownie i spróbuj jeszcze raz.',
  genericStepError: 'Nie udało się ukończyć tego kroku. Spróbuj ponownie.',
  authNone: 'Bez logowania',
  authBearer: 'Klucz dostępu',
  authHeaders: 'Niestandardowe nagłówki',
  authBasic: 'Nazwa użytkownika i hasło',
  authOauth: 'Zaloguj się za pomocą usługi',
  authShopify: 'Aplikacja deweloperska Shopify',
  transportBuiltin: 'Uruchamiane przez Poneva',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Legacy SSE',
  transportAuto: 'Wykryto automatycznie',
  statusNeedsAuth: 'Wymaga logowania',
  statusUnavailable: 'Niedostępne',
  statusConnected: 'Połączono',
  mcpAuthRequired: 'Usługa odrzuciła te dane logowania. Sprawdź je i spróbuj ponownie.',
  mcpUrlBlocked:
    'Użyj publicznego adresu internetowego zaczynającego się od https://. Adresy w sieciach prywatnych nie są obsługiwane.',
  mcpTimeout: 'Usługa zbyt długo nie odpowiadała. Sprawdź adres i spróbuj ponownie.',
  mcpConnectionFailed:
    'Nie udało się nawiązać połączenia. Sprawdź adres internetowy i dane logowania.',
  mcpAuthorizationRequired: 'Zaloguj się ponownie, aby ponownie połączyć tę usługę.',
  oauthNotConfigured:
    'Logowanie w przeglądarce nie jest skonfigurowane w tej instalacji Poneva. Skontaktuj się z administratorem.',
  mcpInvalidRequest: 'Sprawdź adres internetowy i uzupełnij dane dla wybranej metody logowania.',
  shopInvalid:
    'Wpisz adres swojego sklepu w domenie myshopify.com, na przykład your-store.myshopify.com.',
  shopNotFound: 'Shopify nie ma sklepu pod tym adresem myshopify.com.',
  shopNotPermitted:
    'Tej aplikacji nie można używać w tym sklepie. Zainstaluj aplikację w sklepie z Shopify Dev Dashboard i upewnij się, że aplikacja i sklep należą do tej samej organizacji Shopify.',
  shopNotInstalled:
    'Aplikacja nie jest jeszcze zainstalowana w tym sklepie. W Shopify Dev Dashboard otwórz aplikację, wybierz Install app i wskaż ten sklep, a następnie spróbuj ponownie.',
  shopCredentials:
    'Shopify odrzucił identyfikator klienta lub klucz. Skopiuj ponownie oba elementy z ustawień aplikacji w Shopify Dev Dashboard.',
  shopUnavailable: 'Nie udało się połączyć z Shopify. Spróbuj ponownie za chwilę.',
  mcpGenericError: 'Nie udało się zrealizować tego żądania. Spróbuj ponownie.',
};
const pt: Copy = {
  dbSectionTitle: 'Bancos de dados',
  dbSectionIntro:
    'Escolha quem pode usar cada banco de dados nos Chats e Apps. Você pode alterar isso aqui a qualquer momento.',
  dbAccessOff: 'Acesso desativado',
  dbAccessEnabled: 'Acesso ativado para {count} de {total}',
  dbLoading: 'Carregando bancos de dados…',
  dbLoadError:
    'Não foi possível carregar seus bancos de dados. Verifique sua conexão e tente novamente.',
  queryLimitLabel: 'Tempo máximo da consulta',
  queryLimitHint:
    'Por quanto tempo uma leitura pode ser executada antes de ser interrompida. Chats e Apps podem solicitar até esse limite; caso contrário, a leitura será interrompida após {default}.',
  queryLimitDefault: 'Padrão ({value})',
  queryLimitSource: 'Configuração da conexão ({value})',
  queryLimitSaving: 'Salvando…',
  queryLimitSaved: 'Salvo',
  queryLimitError: 'Não foi possível salvar o limite de tempo. Tente novamente.',
  queryLimitLoadError: 'Não foi possível carregar o limite de tempo.',
  queryLimitRetry: 'Tentar novamente',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'Falha na verificação de TLS. Verifique o certificado do servidor ou adicione o certificado da CA em TLS avançado.',
  pgAuthFailed: 'O PostgreSQL rejeitou essas credenciais. Verifique o nome de usuário e a senha.',
  pgNetworkFailed:
    'A Poneva não conseguiu acessar este host. Verifique o nome do host, a porta e o firewall.',
  pgReadOnlyRequired:
    'Use uma réplica somente leitura ou uma conta de banco de dados dedicada sem permissões de administrador, criação de esquemas ou troca de funções.',
  pgNoTables:
    'Nenhuma tabela legível foi encontrada. Escolha um banco de dados com pelo menos uma tabela.',
  pgCatalogLarge:
    'O catálogo deste banco de dados é grande demais. Restrinja o banco de dados ou o esquema antes de tentar novamente.',
  pgCatalogChanged: 'O catálogo do banco de dados foi alterado. Teste a conexão novamente.',
  pgUnsafeHost: 'Este host não é permitido. Use um endpoint PostgreSQL público com TLS.',
  pgSetupBusy:
    'Outra configuração do PostgreSQL já está em andamento. Tente novamente em instantes.',
  invalidRequest: 'Verifique os campos e tente novamente.',
  connectionChanged: 'Esta conexão foi alterada em outro local. Recarregue-a e tente novamente.',
  genericStepError: 'Não foi possível concluir esta etapa. Tente novamente.',
  authNone: 'Sem autenticação',
  authBearer: 'Chave de acesso',
  authHeaders: 'Cabeçalhos personalizados',
  authBasic: 'Nome de usuário e senha',
  authOauth: 'Iniciar sessão com o serviço',
  authShopify: 'Aplicativo de desenvolvedor da Shopify',
  transportBuiltin: 'Executado pela Poneva',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Legacy SSE',
  transportAuto: 'Detetado automaticamente',
  statusNeedsAuth: 'Requer autenticação',
  statusUnavailable: 'Indisponível',
  statusConnected: 'Conectado',
  mcpAuthRequired:
    'O serviço rejeitou estes dados de autenticação. Verifique-os e tente novamente.',
  mcpUrlBlocked:
    'Use um endereço web público que comece com https://. Endereços de redes privadas não são compatíveis.',
  mcpTimeout: 'O serviço demorou demais para responder. Verifique o endereço e tente novamente.',
  mcpConnectionFailed:
    'Não foi possível conectar. Verifique o endereço web e os dados de autenticação.',
  mcpAuthorizationRequired: 'Inicie sessão novamente para reconectar este serviço.',
  oauthNotConfigured:
    'A autenticação pelo navegador não está configurada nesta instalação da Poneva. Entre em contato com o administrador.',
  mcpInvalidRequest:
    'Verifique o endereço web e preencha os dados do método de autenticação selecionado.',
  shopInvalid: 'Insira o endereço myshopify.com da sua loja, por exemplo, sua-loja.myshopify.com.',
  shopNotFound: 'A Shopify não encontrou nenhuma loja nesse endereço myshopify.com.',
  shopNotPermitted:
    'Este aplicativo não pode ser usado nessa loja. Instale o aplicativo na loja pelo Dev Dashboard da Shopify e verifique se o aplicativo e a loja pertencem à mesma organização da Shopify.',
  shopNotInstalled:
    'O aplicativo ainda não está instalado nesta loja. No Dev Dashboard da Shopify, abra o aplicativo, selecione Instalar aplicativo e escolha esta loja. Em seguida, tente novamente.',
  shopCredentials:
    'A Shopify rejeitou o ID ou o segredo do cliente. Copie ambos novamente nas configurações do aplicativo no Dev Dashboard da Shopify.',
  shopUnavailable: 'Não foi possível acessar a Shopify. Tente novamente em instantes.',
  mcpGenericError: 'Não foi possível concluir essa solicitação. Tente novamente.',
};
const ro: Copy = {
  dbSectionTitle: 'Baze de date',
  dbSectionIntro:
    'Alege cine poate folosi fiecare bază de date în conversații și aplicații. Poți schimba acest lucru aici oricând.',
  dbAccessOff: 'Accesul este dezactivat',
  dbAccessEnabled: 'Acces activat pentru {count} din {total}',
  dbLoading: 'Se încarcă bazele de date…',
  dbLoadError: 'Nu am putut încărca bazele tale de date. Verifică conexiunea și încearcă din nou.',
  queryLimitLabel: 'Durata maximă a interogării',
  queryLimitHint:
    'Cât timp poate rula o citire înainte să se oprească. Conversațiile și aplicațiile pot solicita cel mult această durată; în caz contrar, citirea se oprește după {default}.',
  queryLimitDefault: 'Implicit ({value})',
  queryLimitSource: 'Setarea conexiunii ({value})',
  queryLimitSaving: 'Se salvează…',
  queryLimitSaved: 'Salvat',
  queryLimitError: 'Nu am putut salva limita de timp. Încearcă din nou.',
  queryLimitLoadError: 'Nu am putut încărca limita de timp.',
  queryLimitRetry: 'Încearcă din nou',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'Verificarea TLS a eșuat. Verifică certificatul serverului sau adaugă certificatul CA la TLS avansat.',
  pgAuthFailed:
    'PostgreSQL a respins aceste date de autentificare. Verifică numele de utilizator și parola.',
  pgNetworkFailed:
    'Poneva nu a putut accesa această gazdă. Verifică numele gazdei, portul și firewall-ul.',
  pgReadOnlyRequired:
    'Folosește o replică doar pentru citire sau un cont de bază de date dedicat, fără permisiuni de administrator, de creare a schemelor sau de schimbare a rolului.',
  pgNoTables: 'Nu au fost găsite tabele lizibile. Alege o bază de date cu cel puțin un tabel.',
  pgCatalogLarge:
    'Catalogul acestei baze de date este prea mare. Restrânge baza de date sau schema înainte de a încerca din nou.',
  pgCatalogChanged: 'Catalogul bazei de date s-a schimbat. Testează din nou conexiunea.',
  pgUnsafeHost: 'Această gazdă nu este permisă. Folosește un endpoint PostgreSQL public cu TLS.',
  pgSetupBusy:
    'O altă configurare PostgreSQL este deja în curs. Încearcă din nou peste câteva momente.',
  invalidRequest: 'Verifică câmpurile și încearcă din nou.',
  connectionChanged:
    'Această conexiune a fost schimbată în altă parte. Reîncarc-o și încearcă din nou.',
  genericStepError: 'Nu am putut finaliza acest pas. Încearcă din nou.',
  authNone: 'Fără autentificare',
  authBearer: 'Cheie de acces',
  authHeaders: 'Antete personalizate',
  authBasic: 'Nume de utilizator și parolă',
  authOauth: 'Autentificare prin serviciu',
  authShopify: 'Aplicație Shopify pentru dezvoltatori',
  transportBuiltin: 'Rulat de Poneva',
  transportHttp: 'HTTP transmis în flux',
  transportSse: 'SSE vechi',
  transportAuto: 'Detectat automat',
  statusNeedsAuth: 'Necesită autentificare',
  statusUnavailable: 'Indisponibil',
  statusConnected: 'Conectat',
  mcpAuthRequired:
    'Serviciul a respins aceste date de autentificare. Verifică-le și încearcă din nou.',
  mcpUrlBlocked:
    'Folosește o adresă web publică ce începe cu https://. Adresele rețelelor private nu sunt acceptate.',
  mcpTimeout:
    'Serviciului i-a luat prea mult timp să răspundă. Verifică adresa și încearcă din nou.',
  mcpConnectionFailed:
    'Nu am putut realiza conexiunea. Verifică adresa web și datele de autentificare.',
  mcpAuthorizationRequired: 'Autentifică-te din nou pentru a reconecta acest serviciu.',
  oauthNotConfigured:
    'Autentificarea în browser nu este configurată în această instalare Poneva. Contactează administratorul.',
  mcpInvalidRequest:
    'Verifică adresa web și completează detaliile pentru metoda de autentificare selectată.',
  shopInvalid: 'Introdu adresa magazinului tău myshopify.com, de exemplu your-store.myshopify.com.',
  shopNotFound: 'Shopify nu are niciun magazin la această adresă myshopify.com.',
  shopNotPermitted:
    'Această aplicație nu poate fi folosită în acel magazin. Instalează aplicația în magazin din Shopify Dev Dashboard și asigură-te că aplicația și magazinul aparțin aceleiași organizații Shopify.',
  shopNotInstalled:
    'Aplicația nu este încă instalată în acest magazin. În Shopify Dev Dashboard, deschide aplicația, alege Install app și selectează acest magazin, apoi încearcă din nou.',
  shopCredentials:
    'Shopify a respins ID-ul clientului sau secretul. Copiază-le din nou pe ambele din setările aplicației din Shopify Dev Dashboard.',
  shopUnavailable: 'Shopify nu a putut fi accesat. Încearcă din nou peste câteva momente.',
  mcpGenericError: 'Nu am putut finaliza solicitarea. Încearcă din nou.',
};
const sk: Copy = {
  dbSectionTitle: 'Databázy',
  dbSectionIntro:
    'Vyberte, kto môže používať jednotlivé databázy v chatoch a aplikáciách. Toto nastavenie môžete kedykoľvek zmeniť.',
  dbAccessOff: 'Prístup je vypnutý',
  dbAccessEnabled: 'Prístup povolený pre {count} z {total}',
  dbLoading: 'Načítavajú sa databázy…',
  dbLoadError: 'Databázy sa nepodarilo načítať. Skontrolujte pripojenie a skúste to znova.',
  queryLimitLabel: 'Najdlhší čas dotazu',
  queryLimitHint:
    'Ako dlho môže jedno čítanie prebiehať, kým sa zastaví. Chaty a aplikácie môžu požiadať o tento čas; v opačnom prípade sa čítanie zastaví po {default}.',
  queryLimitDefault: 'Predvolené ({value})',
  queryLimitSource: 'Nastavenie pripojenia ({value})',
  queryLimitSaving: 'Ukladá sa…',
  queryLimitSaved: 'Uložené',
  queryLimitError: 'Časový limit sa nepodarilo uložiť. Skúste to znova.',
  queryLimitLoadError: 'Časový limit sa nepodarilo načítať.',
  queryLimitRetry: 'Skúsiť znova',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'Overenie TLS zlyhalo. Skontrolujte certifikát servera alebo pridajte jeho certifikačnú autoritu v časti Rozšírené TLS.',
  pgAuthFailed:
    'PostgreSQL odmietol tieto prihlasovacie údaje. Skontrolujte používateľské meno a heslo.',
  pgNetworkFailed:
    'Poneva sa nemohla pripojiť k tomuto hostiteľovi. Skontrolujte názov hostiteľa, port a firewall.',
  pgReadOnlyRequired:
    'Použite repliku iba na čítanie alebo vyhradené databázové konto bez oprávnení správcu, vytvárania schém či prepínania rolí.',
  pgNoTables: 'Nenašli sa žiadne čitateľné tabuľky. Vyberte databázu aspoň s jednou tabuľkou.',
  pgCatalogLarge:
    'Katalóg tejto databázy je príliš veľký. Pred opakovaním zúžte databázu alebo schému.',
  pgCatalogChanged: 'Katalóg databázy sa zmenil. Znova otestujte pripojenie.',
  pgUnsafeHost:
    'Tento hostiteľ nie je povolený. Použite verejne dostupný koncový bod PostgreSQL s TLS.',
  pgSetupBusy: 'Iné nastavenie PostgreSQL už prebieha. Skúste to znova o chvíľu.',
  invalidRequest: 'Skontrolujte polia a skúste to znova.',
  connectionChanged: 'Toto pripojenie bolo zmenené inde. Znova ho načítajte a skúste to znova.',
  genericStepError: 'Tento krok sa nepodarilo dokončiť. Skúste to znova.',
  authNone: 'Bez prihlásenia',
  authBearer: 'Prístupový kľúč',
  authHeaders: 'Vlastné hlavičky',
  authBasic: 'Používateľské meno a heslo',
  authOauth: 'Prihlásiť sa pomocou služby',
  authShopify: 'Shopify Developer App',
  transportBuiltin: 'Spúšťa Poneva',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Legacy SSE',
  transportAuto: 'Zistené automaticky',
  statusNeedsAuth: 'Vyžaduje prihlásenie',
  statusUnavailable: 'Nedostupné',
  statusConnected: 'Pripojené',
  mcpAuthRequired: 'Služba odmietla tieto prihlasovacie údaje. Skontrolujte ich a skúste to znova.',
  mcpUrlBlocked:
    'Použite verejne dostupnú webovú adresu začínajúcu na https://. Adresy v súkromných sieťach nie sú podporované.',
  mcpTimeout: 'Služba odpovedala príliš neskoro. Skontrolujte adresu a skúste to znova.',
  mcpConnectionFailed: 'Nepodarilo sa pripojiť. Skontrolujte webovú adresu a prihlasovacie údaje.',
  mcpAuthorizationRequired: 'Znova sa prihláste a opätovne pripojte túto službu.',
  oauthNotConfigured:
    'Prihlásenie v prehliadači nie je v tejto inštalácii Poneva nakonfigurované. Obráťte sa na správcu.',
  mcpInvalidRequest: 'Skontrolujte webovú adresu a vyplňte údaje pre vybraný spôsob prihlásenia.',
  shopInvalid: 'Zadajte adresu svojho obchodu myshopify.com, napríklad your-store.myshopify.com.',
  shopNotFound: 'Shopify nemá obchod na tejto adrese myshopify.com.',
  shopNotPermitted:
    'Túto aplikáciu nie je možné použiť v tomto obchode. Nainštalujte aplikáciu do obchodu z Shopify’s Dev Dashboard a skontrolujte, či aplikácia a obchod patria do rovnakej organizácie Shopify.',
  shopNotInstalled:
    'Aplikácia zatiaľ nie je v tomto obchode nainštalovaná. V Shopify’s Dev Dashboard otvorte aplikáciu, vyberte Install app a zvoľte tento obchod. Potom to skúste znova.',
  shopCredentials:
    'Shopify odmietol ID klienta alebo tajný kľúč. Skopírujte obe hodnoty znova z nastavení aplikácie v Shopify’s Dev Dashboard.',
  shopUnavailable: 'Shopify sa nepodarilo kontaktovať. Skúste to znova o chvíľu.',
  mcpGenericError: 'Požiadavku sa nepodarilo dokončiť. Skúste to znova.',
};
const sl: Copy = {
  dbSectionTitle: 'Podatkovne zbirke',
  dbSectionIntro:
    'Izberite, kdo lahko uporablja posamezno podatkovno zbirko v klepetih in aplikacijah. To lahko kadar koli spremenite tukaj.',
  dbAccessOff: 'Dostop je izklopljen',
  dbAccessEnabled: 'Dostop je omogočen za {count} od {total}',
  dbLoading: 'Nalaganje podatkovnih zbirk…',
  dbLoadError: 'Podatkovnih zbirk ni bilo mogoče naložiti. Preverite povezavo in poskusite znova.',
  queryLimitLabel: 'Najdaljši čas izvajanja poizvedbe',
  queryLimitHint:
    'Kako dolgo se lahko izvaja eno branje, preden se ustavi. Klepeti in aplikacije lahko zahtevajo največ toliko časa; sicer se branje ustavi po {default}.',
  queryLimitDefault: 'Privzeto ({value})',
  queryLimitSource: 'Nastavitev povezave ({value})',
  queryLimitSaving: 'Shranjevanje…',
  queryLimitSaved: 'Shranjeno',
  queryLimitError: 'Časovne omejitve ni bilo mogoče shraniti. Poskusite znova.',
  queryLimitLoadError: 'Časovne omejitve ni bilo mogoče naložiti.',
  queryLimitRetry: 'Poskusite znova',
  seconds: '{count} s',
  minutes: '{count} min',
  pgTlsFailed:
    'Preverjanje TLS ni uspelo. Preverite potrdilo strežnika ali dodajte njegovo potrdilo CA v razdelku Napredne nastavitve TLS.',
  pgAuthFailed: 'PostgreSQL je zavrnil te poverilnice. Preverite uporabniško ime in geslo.',
  pgNetworkFailed:
    'Poneva ni mogla doseči tega gostitelja. Preverite ime gostitelja, vrata in požarni zid.',
  pgReadOnlyRequired:
    'Uporabite repliko samo za branje ali namenski račun podatkovne zbirke brez skrbniških dovoljenj, dovoljenj za ustvarjanje shem ali preklapljanje vlog.',
  pgNoTables:
    'Najdena ni bila nobena tabela, ki bi jo bilo mogoče brati. Izberite podatkovno zbirko z vsaj eno tabelo.',
  pgCatalogLarge:
    'Katalog te podatkovne zbirke je prevelik. Pred ponovnim poskusom omejite podatkovno zbirko ali shemo.',
  pgCatalogChanged: 'Katalog podatkovne zbirke se je spremenil. Znova preizkusite povezavo.',
  pgUnsafeHost: 'Ta gostitelj ni dovoljen. Uporabite javno končno točko PostgreSQL s TLS.',
  pgSetupBusy: 'Nastavitev drugega PostgreSQL je že v teku. Čez trenutek poskusite znova.',
  invalidRequest: 'Preverite polja in poskusite znova.',
  connectionChanged:
    'Ta povezava je bila spremenjena drugje. Znova jo naložite in poskusite znova.',
  genericStepError: 'Tega koraka ni bilo mogoče dokončati. Poskusite znova.',
  authNone: 'Brez prijave',
  authBearer: 'Dostopni ključ',
  authHeaders: 'Glave po meri',
  authBasic: 'Uporabniško ime in geslo',
  authOauth: 'Prijava v storitev',
  authShopify: 'Aplikacija Shopify za razvijalce',
  transportBuiltin: 'Zažene Poneva',
  transportHttp: 'Pretočni HTTP',
  transportSse: 'Starejši SSE',
  transportAuto: 'Samodejno zaznano',
  statusNeedsAuth: 'Zahteva prijavo',
  statusUnavailable: 'Ni na voljo',
  statusConnected: 'Povezano',
  mcpAuthRequired: 'Storitev je zavrnila te podatke za prijavo. Preverite jih in poskusite znova.',
  mcpUrlBlocked:
    'Uporabite javni spletni naslov, ki se začne z https://. Naslovi zasebnih omrežij niso podprti.',
  mcpTimeout: 'Storitev se je odzivala predolgo. Preverite naslov in poskusite znova.',
  mcpConnectionFailed:
    'Povezave ni bilo mogoče vzpostaviti. Preverite spletni naslov in podatke za prijavo.',
  mcpAuthorizationRequired: 'Za ponovno povezavo s to storitvijo se znova prijavite.',
  oauthNotConfigured:
    'Prijava prek brskalnika v tej namestitvi Poneva ni konfigurirana. Obrnite se na skrbnika.',
  mcpInvalidRequest: 'Preverite spletni naslov in vnesite podatke za izbrani način prijave.',
  shopInvalid: 'Vnesite naslov svoje trgovine myshopify.com, na primer your-store.myshopify.com.',
  shopNotFound: 'Shopify na tem naslovu myshopify.com nima trgovine.',
  shopNotPermitted:
    'Te aplikacije ni mogoče uporabljati v tej trgovini. Namestite jo v trgovino prek Shopifyjeve nadzorne plošče za razvijalce in preverite, ali aplikacija in trgovina pripadata isti organizaciji Shopify.',
  shopNotInstalled:
    'Aplikacija še ni nameščena v tej trgovini. V Shopifyjevi nadzorni plošči za razvijalce odprite aplikacijo, izberite Namesti aplikacijo in nato to trgovino, potem pa poskusite znova.',
  shopCredentials:
    'Shopify je zavrnil ID ali skrivnost odjemalca. Oboje znova kopirajte iz nastavitev aplikacije v Shopifyjevi nadzorni plošči za razvijalce.',
  shopUnavailable: 'Shopify ni bil dosegljiv. Čez trenutek poskusite znova.',
  mcpGenericError: 'Te zahteve ni bilo mogoče dokončati. Poskusite znova.',
};
const sv: Copy = {
  dbSectionTitle: 'Databaser',
  dbSectionIntro:
    'Välj vem som får använda varje databas i chattar och appar. Du kan ändra detta här när som helst.',
  dbAccessOff: 'Åtkomst avstängd',
  dbAccessEnabled: 'Åtkomst aktiverad för {count} av {total}',
  dbLoading: 'Läser in databaser…',
  dbLoadError: 'Vi kunde inte läsa in dina databaser. Kontrollera anslutningen och försök igen.',
  queryLimitLabel: 'Längsta frågetid',
  queryLimitHint:
    'Så länge får en läsning köras innan den stoppas. Chattar och appar kan begära upp till denna tid; annars stoppas en läsning efter {default}.',
  queryLimitDefault: 'Standard ({value})',
  queryLimitSource: 'Anslutningsinställning ({value})',
  queryLimitSaving: 'Sparar…',
  queryLimitSaved: 'Sparad',
  queryLimitError: 'Vi kunde inte spara tidsgränsen. Försök igen.',
  queryLimitLoadError: 'Vi kunde inte läsa in tidsgränsen.',
  queryLimitRetry: 'Försök igen',
  seconds: '{count} sek',
  minutes: '{count} min',
  pgTlsFailed:
    'TLS-verifieringen misslyckades. Kontrollera servercertifikatet eller lägg till dess CA-certifikat under Avancerad TLS.',
  pgAuthFailed:
    'PostgreSQL avvisade dessa inloggningsuppgifter. Kontrollera användarnamnet och lösenordet.',
  pgNetworkFailed:
    'Poneva kunde inte nå den här värden. Kontrollera värdnamnet, porten och brandväggen.',
  pgReadOnlyRequired:
    'Använd en skrivskyddad replika eller ett särskilt databaskonto utan behörighet att administrera, skapa scheman eller byta roll.',
  pgNoTables: 'Inga läsbara tabeller hittades. Välj en databas med minst en tabell.',
  pgCatalogLarge:
    'Den här databaskatalogen är för stor. Begränsa databasen eller schemat innan du försöker igen.',
  pgCatalogChanged: 'Databaskatalogen har ändrats. Testa anslutningen igen.',
  pgUnsafeHost:
    'Den här värden är inte tillåten. Använd en offentlig TLS-slutpunkt för PostgreSQL.',
  pgSetupBusy: 'En annan PostgreSQL-konfiguration pågår redan. Försök igen om en stund.',
  invalidRequest: 'Kontrollera fälten och försök igen.',
  connectionChanged:
    'Den här anslutningen har ändrats någon annanstans. Läs in den igen och försök igen.',
  genericStepError: 'Vi kunde inte slutföra det här steget. Försök igen.',
  authNone: 'Ingen inloggning',
  authBearer: 'Åtkomstnyckel',
  authHeaders: 'Anpassade rubriker',
  authBasic: 'Användarnamn och lösenord',
  authOauth: 'Logga in med tjänsten',
  authShopify: 'Shopify Developer App',
  transportBuiltin: 'Körs av Poneva',
  transportHttp: 'Streamable HTTP',
  transportSse: 'Legacy SSE',
  transportAuto: 'Identifieras automatiskt',
  statusNeedsAuth: 'Inloggning krävs',
  statusUnavailable: 'Otillgänglig',
  statusConnected: 'Ansluten',
  mcpAuthRequired: 'Tjänsten avvisade dessa inloggningsuppgifter. Kontrollera dem och försök igen.',
  mcpUrlBlocked:
    'Använd en offentlig webbadress som börjar med https://. Adresser i privata nätverk stöds inte.',
  mcpTimeout: 'Tjänsten tog för lång tid på sig att svara. Kontrollera adressen och försök igen.',
  mcpConnectionFailed:
    'Vi kunde inte ansluta. Kontrollera webbadressen och inloggningsuppgifterna.',
  mcpAuthorizationRequired: 'Logga in igen för att återansluta den här tjänsten.',
  oauthNotConfigured:
    'Webbläsarinloggning är inte konfigurerad i den här Poneva-installationen. Kontakta administratören.',
  mcpInvalidRequest:
    'Kontrollera webbadressen och fyll i uppgifterna för den valda inloggningsmetoden.',
  shopInvalid: 'Ange butikens myshopify.com-adress, till exempel your-store.myshopify.com.',
  shopNotFound: 'Shopify har ingen butik på den myshopify.com-adressen.',
  shopNotPermitted:
    'Den här appen kan inte användas i den butiken. Installera appen i butiken från Shopifys Dev Dashboard och se till att appen och butiken tillhör samma Shopify-organisation.',
  shopNotInstalled:
    'Appen är inte installerad i den här butiken ännu. Öppna appen i Shopifys Dev Dashboard, välj Install app och välj den här butiken. Försök sedan igen.',
  shopCredentials:
    'Shopify avvisade klient-ID:t eller hemligheten. Kopiera båda igen från appens inställningar i Shopifys Dev Dashboard.',
  shopUnavailable: 'Shopify kunde inte nås. Försök igen om en stund.',
  mcpGenericError: 'Vi kunde inte slutföra den begäran. Försök igen.',
};

const copy: NamespaceCatalog<keyof typeof en> = {
  en,
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};
export default copy;
